import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Select,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
  useToken,
  VStack,
} from '@chakra-ui/react';
import { AxiosError } from 'axios';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import ReactSelect from 'react-select';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import { useCustomFields } from '../../../hooks/useCustomFields';
import {
  Department,
  getDepartments,
} from '../../../services/departments/getDepartments';
import { getPracticeById } from '../../../services/practices/getPracticeById';
import { createProvider } from '../../../services/providers/createProvider';
import { getProviderById } from '../../../services/providers/getProviderById';
import { updateProviderById } from '../../../services/providers/updateProviderById';
import {
  CustomFieldValue,
  Practice,
  Provider,
  ProviderBridgeItem as BaseBridgeItem,
  ProviderCustomFields,
} from '../../../types';
import { CustomField } from '../../../types/custom-fields/types';
import {
  isRootFieldUnchanged,
  pruneUnchangedCustomFields,
} from '../../../utils/customFieldDiff';
import {
  formatProviderName,
  getClientIdWithFallback,
  getProviderCustomFieldLabel,
  getProviderFieldPlaceholderLabel,
  getCustomFieldValue,
} from '../../../utils/customFieldUtils';
import {
  getSuffixOptions,
  getTitleOptions,
} from '../../../utils/getOptions';
import {
  CanonProviderType,
  getProviderTypeFromData,
} from '../../../utils/getProviderTypeFromData';
import {
  PCP_COVERAGE_COMPLEX_FIELDS,
  PCP_ONLY_FIELDS,
  SPECIALIST_ONLY_FIELDS,
} from '../../../utils/providerCustomFields';
import { scrollToTop } from '../../../utils/scrollUtils';
import { getOptionsFromAllowedValues } from '../../../utils/buildFilterOptionsFromCustomFields';
import { RICH_TEXT_FIELDS } from '../../../utils/providerFieldMapping';
import { getVisibleText } from '../../../utils/textConversion';

import { FormFieldErrorBoundary } from '../../../components/error-boundary/FormFieldErrorBoundary';
import RichTextEditor from '../../../components/rich-text-editor/RichTextEditor';
import { normalizeYesNo } from '../../../utils/normalizeYesNo';
import { CrossCoverageAllApptsSelect } from './CrossCoverageAllApptsSelect';
import { ClosestUrgentCareSelect } from './ClosestUrgentCareSelect';
import { CrossCoverageSickSelect } from './CrossCoverageSickSelect';
import { COVERAGE_LABELS } from './coverageTypes';
import { MedicareWellnessSelect } from './MedicareWellnessSelect';
import PracticeSearchSelect from './PracticeSearchSelect';
import { SupervisingPhysicianSelect } from './SupervisingPhysicianSelect';
import { createGrid } from '../../../services/plans/createGrid';
import { getPlansList } from '../../../services/plans/listPlans';
import { fetchSourceInsurancePlans, prepareGridEntries } from '../../../utils/insuranceCopyUtils';
import NewProviderInsuranceSetup from '../../../components/new-provider-insurance-setup/NewProviderInsuranceSetup';

type FormDataObject =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean | null | undefined>
  | CustomFieldValue;
type DropdownOption = {
  label: string;
  value: string;
};
type FieldConfig = {
  label: string;
  name: string;
  type?: 'input' | 'select' | 'textarea';
  options?: DropdownOption[];
};

type ProviderBridgeItem = BaseBridgeItem & { tempName?: string };

function getInitialProviderFormValues(
  providerData: Provider,
  customFields: CustomField[]
): Record<string, string | string[] | CustomFieldValue> {
  const initialValues: Record<
    string,
    string | string[] | CustomFieldValue
  > = {};

  if (!providerData) return initialValues;

  // Map root fields
  initialValues.npi = providerData.npi;
  initialValues.firstname =
    providerData.firstName || providerData.firstname || '';
  initialValues.middlename = providerData.middlename ?? '';
  initialValues.lastname =
    providerData.lastName || providerData.lastname || '';
  initialValues.suffix = providerData.suffix ?? '';
  initialValues.title = providerData.title ?? '';
  initialValues.specialty = providerData.specialty
    ? providerData.specialty
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
    : [];
  initialValues.nickname = providerData.nickname ?? ''; // Add nickname
  initialValues.pronunciation = providerData.pronunciation ?? ''; // Add pronunciation

  // Map Credentials custom field
  const credentialsValue = getCustomFieldValue(
    providerData.customFields,
    'Credentials'
  );
  initialValues.Credentials = credentialsValue || '';

  // Handle relatedPractices
  initialValues.relatedPractices =
    providerData.relatedPractices?.map((practice) => practice.id) ||
    [];

  // Map custom fields
  customFields.forEach((cf) => {
    // Special-case for Medicare-Wellness: set to full CustomFieldValue row
    const medicareArr =
      providerData.customFields?.['Medicare-Wellness'];
    if (
      cf.name === 'Medicare-Wellness' &&
      Array.isArray(medicareArr) &&
      medicareArr.length > 0
    ) {
      const row = medicareArr[0];
      initialValues[cf.name] = {
        value: row.value,
        metadata: row.metadata ?? null,
        id: row.id ?? undefined,
        rowId: row.rowId ?? undefined,
        fieldId: row.fieldId ?? undefined,
        startDate: row.startDate ?? null,
        endDate: row.endDate ?? null,
      } as CustomFieldValue;
      return;
    }

    // Special-case for New-Telehealth: set to full CustomFieldValue row
    const newTeleArr = providerData.customFields?.['New-Telehealth'];
    if (
      cf.name === 'New-Telehealth' &&
      Array.isArray(newTeleArr) &&
      newTeleArr.length > 0
    ) {
      const row = newTeleArr[0];
      initialValues[cf.name] = {
        value: row.value,
        metadata: row.metadata ?? null,
        id: row.id ?? undefined,
        rowId: row.rowId ?? undefined,
        fieldId: row.fieldId ?? undefined,
        startDate: row.startDate ?? null,
        endDate: row.endDate ?? null,
      } as CustomFieldValue;
      return;
    }

    // Special-case for Out-Of-State-Telehealth: set to full CustomFieldValue row
    const outOfStateArr =
      providerData.customFields?.['Out-Of-State-Telehealth'];
    if (
      cf.name === 'Out-Of-State-Telehealth' &&
      Array.isArray(outOfStateArr) &&
      outOfStateArr.length > 0
    ) {
      const row = outOfStateArr[0];
      initialValues[cf.name] = {
        value: row.value,
        metadata: row.metadata ?? null,
        id: row.id ?? undefined,
        rowId: row.rowId ?? undefined,
        fieldId: row.fieldId ?? undefined,
        startDate: row.startDate ?? null,
        endDate: row.endDate ?? null,
      } as CustomFieldValue;
      return;
    }

    // Special-case for Telehealth: set to full CustomFieldValue row
    const telehealthArr = providerData.customFields?.['Telehealth'];
    if (
      cf.name === 'Telehealth' &&
      Array.isArray(telehealthArr) &&
      telehealthArr.length > 0
    ) {
      const row = telehealthArr[0];
      initialValues[cf.name] = {
        value: row.value,
        metadata: row.metadata ?? null,
        id: row.id ?? undefined,
        rowId: row.rowId ?? undefined,
        fieldId: row.fieldId ?? undefined,
        startDate: row.startDate ?? null,
        endDate: row.endDate ?? null,
      } as CustomFieldValue;
      return;
    }

    // Special-case for the PCP coverage fields: set to full CustomFieldValue row
    if (
      PCP_COVERAGE_COMPLEX_FIELDS.includes(cf.name) &&
      Array.isArray(providerData.customFields?.[cf.name]) &&
      (providerData.customFields[cf.name] as CustomFieldValue[])
        .length > 0
    ) {
      const existingFields = providerData.customFields[
        cf.name
      ] as CustomFieldValue[];
      // Find the entry with a non-empty value, or use the first one
      const row =
        existingFields.find(
          (field) => typeof field.value === 'string' && field.value.trim() !== ''
        ) || existingFields[0];
      initialValues[cf.name] = {
        value: row.value,
        metadata: row.metadata ?? null,
        id: row.id ?? undefined,
        rowId: row.rowId ?? undefined,
        fieldId: row.fieldId ?? undefined,
        startDate: row.startDate ?? null,
        endDate: row.endDate ?? null,
      } as CustomFieldValue;
      return;
    }

    // Special-case for Cross-Coverage: set to full CustomFieldValue row
    if (
      cf.name === 'Cross-Coverage' &&
      Array.isArray(providerData.customFields?.['Cross-Coverage']) &&
      providerData.customFields['Cross-Coverage'].length > 0
    ) {
      const existingFields =
        providerData.customFields['Cross-Coverage'];
      // Find the entry with a non-empty value, or use the first one
      const row =
        existingFields.find(
          (field) => typeof field.value === 'string' && field.value.trim() !== ''
        ) || existingFields[0];

      // Ensure metadata uses the correct camelCase structure
      let normalizedMetadata = null;
      if (row.metadata) {
        normalizedMetadata = {
          whoProvidesCoverage:
            row.metadata.whoProvidesCoverage ||
            row.metadata.who ||
            '',
          whatAppointments:
            row.metadata.whatAppointments || row.metadata.what || '',
        };
      }

      initialValues[cf.name] = {
        value: row.value,
        metadata: normalizedMetadata,
        id: row.id ?? undefined,
        rowId: row.rowId ?? undefined,
        fieldId: row.fieldId ?? undefined,
        startDate: row.startDate ?? null,
        endDate: row.endDate ?? null,
      } as CustomFieldValue;
      return;
    }

    // Special-case for Supervision-Physician: set to full CustomFieldValue row
    if (
      cf.name === 'Supervision-Physician' &&
      Array.isArray(
        providerData.customFields?.['Supervision-Physician']
      ) &&
      providerData.customFields['Supervision-Physician'].length > 0
    ) {
      const existingFields =
        providerData.customFields['Supervision-Physician'];
      const row = existingFields[0];

      initialValues[cf.name] = {
        value: row.value,
        metadata: row.metadata ?? null,
        id: row.id ?? undefined,
        rowId: row.rowId ?? undefined,
        fieldId: row.fieldId ?? undefined,
        startDate: row.startDate ?? null,
        endDate: row.endDate ?? null,
      } as CustomFieldValue;
      return;
    }

    // Special-case for Scheduling-Notes: set to simple string value
    if (
      cf.name === 'Scheduling-Notes' &&
      Array.isArray(
        providerData.customFields?.['Scheduling-Notes']
      ) &&
      providerData.customFields['Scheduling-Notes'].length > 0
    ) {
      const row = providerData.customFields['Scheduling-Notes'][0];
      initialValues[cf.name] = row.metadata?.htmlContent || row.value || '';
      return;
    }

    // For rich text fields, prefer metadata.htmlContent over value
    if (RICH_TEXT_FIELDS.includes(cf.name as (typeof RICH_TEXT_FIELDS)[number])) {
      const cfArr = providerData.customFields?.[cf.name] as
        | CustomFieldValue[]
        | undefined;
      initialValues[cf.name] =
        cfArr?.[0]?.metadata?.htmlContent || cfArr?.[0]?.value || '';
      return;
    }

    // Handle other custom fields with simple value extraction
    const raw = getCustomFieldValue(
      providerData.customFields,
      cf.name
    );
    if (raw !== undefined && raw !== null && raw !== '') {
      initialValues[cf.name] = raw;
    } else {
      initialValues[cf.name] = '';
    }
  });

  return initialValues;
}

// const SPECIALTY_OPTIONS = [
//   'Family Medicine',
//   'Family Medicine & Spine Care',
//   'Family Practice',
//   'Family medicine',
//   'GYN',
//   'GYN/Midwifery',
//   'IM & Geriatrics',
//   'Internal Med',
//   'Internal Medicine',
//   'OB/GYN',
//   'OBGYN',
//   'Pediactrics',
//   'Pediatrics',
//   'Peds',
// ];

const CreateProvider = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });
  const toast = useToast();
  const { id: providerId } = useParams<{ id: string }>();
  const isEditMode = !!providerId;
  const navigate = useNavigate();
  const cancelDialog = useDisclosure();
  const cancelRef = useRef(null);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [practiceError, setPracticeError] = useState(false);
  const [providerData, setProviderData] = useState<Provider | null>(
    null
  );
  const [providerError, setProviderError] = useState<Error | null>(
    null
  );
  const [leftColumnFields, setLeftColumnFields] = useState<
    FieldConfig[]
  >([]);
  const [rightColumnFields, setRightColumnFields] = useState<
    FieldConfig[]
  >([]);
  const specialtyAllowedOptions = useMemo(() => {
    const specialtyField = customFields.find(
      (field) => field.name === 'Specialty'
    );
    if (!specialtyField?.allowed_values?.length) return [];
    return specialtyField.allowed_values
      .map((value) => ({
        label: value.value,
        value: value.value,
      }))
      .filter(
        (option) => option.value && option.value.trim().length > 0
      );
  }, [customFields]);
  const [
    tagBg,
    tagHoverBg,
    tagTextColor,
    controlBorderColor,
    controlFocusBorderColor,
    placeholderColor,
    chipRemoveColor,
  ] = useToken('colors', [
    'blue.50',
    'blue.100',
    'blue.700',
    'gray.300',
    'blue.400',
    'gray.500',
    'blue.500',
  ]);

  const specialtySelectStyles = useMemo(
    () => ({
      control: (
        base: Record<string, unknown>,
        state: { isFocused: boolean }
      ) => ({
        ...base,
        borderColor: state.isFocused
          ? controlFocusBorderColor
          : controlBorderColor,
        boxShadow: state.isFocused
          ? `0 0 0 1px ${controlFocusBorderColor}`
          : 'none',
        minHeight: '2.5rem',
        '&:hover': {
          borderColor: controlFocusBorderColor,
        },
      }),
      placeholder: (base: Record<string, unknown>) => ({
        ...base,
        color: placeholderColor,
        fontSize: '0.95rem',
      }),
      multiValue: (base: Record<string, unknown>) => ({
        ...base,
        backgroundColor: tagBg,
        borderRadius: '0.5rem',
        paddingRight: '0.125rem',
      }),
      multiValueLabel: (base: Record<string, unknown>) => ({
        ...base,
        color: tagTextColor,
        fontWeight: 600,
        fontSize: '0.875rem',
        paddingRight: '0.25rem',
      }),
      multiValueRemove: (
        base: Record<string, unknown>,
        state: { isFocused: boolean }
      ) => ({
        ...base,
        color: chipRemoveColor,
        borderRadius: '0.5rem',
        backgroundColor: state.isFocused ? tagHoverBg : tagBg,
        ':hover': {
          backgroundColor: tagHoverBg,
          color: chipRemoveColor,
        },
      }),
      option: (
        base: Record<string, unknown>,
        state: { isFocused: boolean; isSelected: boolean }
      ) => ({
        ...base,
        backgroundColor: state.isSelected
          ? tagBg
          : state.isFocused
          ? tagHoverBg
          : 'white',
        color: state.isSelected ? tagTextColor : 'inherit',
      }),
      menu: (base: Record<string, unknown>) => ({
        ...base,
        zIndex: 10,
      }),
    }),
    [
      chipRemoveColor,
      controlBorderColor,
      controlFocusBorderColor,
      placeholderColor,
      tagBg,
      tagHoverBg,
      tagTextColor,
    ]
  );
  const [selectedPractices, setSelectedPractices] = useState<
    { label: string; value: string }[]
  >([]);
  const [providerBridge, setProviderBridge] = useState<
    ProviderBridgeItem[]
  >([]);
  const [practiceDataMap, setPracticeDataMap] = useState<
    Record<string, Practice>
  >({});
  const [departmentsByTablespace, setDepartmentsByTablespace] = useState<
    Record<string, Department[]>
  >({});
  const [loadingDepartments, setLoadingDepartments] = useState<
    Record<string, boolean>
  >({});
  const [selectedDeptForAutofill, setSelectedDeptForAutofill] = useState<
    Record<string, string>
  >({});
  const departmentsByTablespaceRef = useRef<Record<string, Department[]>>({});

  // Sync ref with state
  useEffect(() => {
    departmentsByTablespaceRef.current = departmentsByTablespace;
  }, [departmentsByTablespace]);

  // Function to fetch departments for a tablespace
  // This is ONLY for the Department Autofill Helper feature in Practice Details section
  // It calls getDepartments API - completely separate from Resource Provider Search feature
  const fetchDepartments = useCallback(
    async (tablespace: string) => {
      if (!tablespace || tablespace.trim() === '') {
        return;
      }

      const tablespaceKey = tablespace.trim();

      // Check if we already have the departments
      if (departmentsByTablespaceRef.current[tablespaceKey]) {
        return; // Already have departments, don't fetch
      }

      // Set loading state
      setLoadingDepartments((prev) => ({
        ...prev,
        [tablespaceKey]: true,
      }));

      try {
        // IMPORTANT: This calls getDepartments API, NOT getResourceProviders
        // These are two separate features with separate APIs
        const response = await getDepartments(tablespaceKey);

        // Check if response has departments array (even if empty)
        if (response.departments) {
          // Convert string array to Department objects if needed
          const departments: Department[] = response.departments.map(
            (dept, index) => {
              if (typeof dept === 'string') {
                return {
                  id: `dept-${index}`,
                  name: dept,
                };
              }
              return dept;
            }
          );

          // Handle empty departments array
          if (departments.length === 0) {
            toast({
              title: 'No departments found',
              description: `No Athena departments found for tablespace ${tablespaceKey}. You can still type the department name manually.`,
              status: 'info',
              duration: 5000,
              isClosable: true,
            });
            // Still set empty array to prevent re-fetching
            setDepartmentsByTablespace((prev) => ({
              ...prev,
              [tablespaceKey]: [],
            }));
          } else {
            setDepartmentsByTablespace((prev) => {
              // Double-check we don't already have it (race condition prevention)
              if (prev[tablespaceKey]) {
                return prev;
              }
              return {
                ...prev,
                [tablespaceKey]: departments,
              };
            });

            // Show success toast
            toast({
              title: 'Departments loaded',
              description: `Found ${departments.length} department${
                departments.length !== 1 ? 's' : ''
              } for tablespace ${tablespaceKey}`,
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }
        } else {
          // Response doesn't have departments property
          toast({
            title: 'No departments found',
            description: `No Athena departments found for tablespace ${tablespaceKey}. You can still type the department name manually.`,
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        // Show error toast but allow free text entry as fallback
        toast({
          title: 'Could not load departments',
          description:
            'You can still type the department name manually',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoadingDepartments((prev) => ({
          ...prev,
          [tablespaceKey]: false,
        }));
      }
    },
    [toast]
  );

  // Combine providerData practices with practiceDataMap for MedicareWellnessSelect
  const allPracticesForMedicare = useMemo(() => {
    const practicesFromProvider =
      providerData?.relatedPractices || [];
    const practicesFromMap = Object.values(practiceDataMap);

    // Merge: use practiceDataMap if available, otherwise use providerData practices
    const merged = new Map<string, Practice>();
    practicesFromProvider.forEach((p) => {
      merged.set(p.id, p);
    });
    practicesFromMap.forEach((p) => {
      merged.set(p.id, p);
    });

    return Array.from(merged.values());
  }, [providerData?.relatedPractices, practiceDataMap]);

  // Store complex custom field objects separately from form values
  const [complexCustomFields, setComplexCustomFields] = useState<
    Record<string, CustomFieldValue>
  >({});

  const watchedProviderType = watch('Provider-Type');
  const watchedNpi = watch('npi');
  const watchedFirstname = watch('firstname');
  const watchedLastname = watch('lastname');

  // Form validation function
  const isFormValidForSubmission = () => {
    const hasProviderType =
      watchedProviderType &&
      watchedProviderType.toString().trim() !== '';
    const hasRequiredFields =
      watchedNpi && watchedFirstname && watchedLastname;
    const hasPractice = isEditMode || providerBridge.length > 0;

    return hasProviderType && hasRequiredFields && hasPractice;
  };

  const rawPTWatch = (watchedProviderType || '')
    .toString()
    .trim()
    .toLowerCase();
  const providerType: CanonProviderType =
    rawPTWatch === 'pcp'
      ? 'Pcp'
      : rawPTWatch === 'specialist' || rawPTWatch === 'specialty'
      ? 'Specialist'
      : '';

  const fetchProvider = useCallback(async () => {
    if (!providerId) return;

    try {
      setProviderError(null);
      const response = await getProviderById(providerId);
      if (response.data?.provider) {
        setProviderData(response.data.provider);
      } else {
        throw new Error('Provider not found');
      }
    } catch (error) {
      setProviderError(
        error instanceof Error
          ? error
          : new Error('Failed to fetch provider')
      );
    }
  }, [providerId]);

  // Fetch provider data in edit mode
  useEffect(() => {
    if (isEditMode && providerId) {
      fetchProvider();
    }
  }, [isEditMode, providerId, fetchProvider]);

  // Initialize complex custom fields when editing a provider
  useEffect(() => {
    if (isEditMode && providerData && providerData.customFields) {
      const customFieldsData =
        providerData.customFields as ProviderCustomFields;

      // Initialize Medicare-Wellness complex field
      const medicareWellnessField =
        customFieldsData['Medicare-Wellness'];
      if (
        Array.isArray(medicareWellnessField) &&
        medicareWellnessField.length > 0
      ) {
        const medicareField = medicareWellnessField[0];
        setComplexCustomFields((prev) => ({
          ...prev,
          'Medicare-Wellness': {
            id: medicareField.id,
            fieldId: medicareField.fieldId,
            value: medicareField.value || '',
            startDate: medicareField.startDate,
            endDate: medicareField.endDate,
            metadata: medicareField.metadata,
            rowId: medicareField.rowId,
          },
        }));
      }

      // Initialize Cross-Coverage-Sick complex field
      const crossCoverageSickField =
        customFieldsData['Cross-Coverage-Sick'];
      if (
        Array.isArray(crossCoverageSickField) &&
        crossCoverageSickField.length > 0
      ) {
        const crossCoverageField =
          crossCoverageSickField.find((r) => {
            const v = typeof r.value === 'string' ? r.value.trim() : '';
            const ct = r.metadata?.coverageType as string | undefined;
            return (
              (v !== '' && v !== 'None') ||
              Boolean(ct && COVERAGE_LABELS[ct]) ||
              (Array.isArray(r.metadata?.providers) &&
                (r.metadata.providers as unknown[]).length > 0)
            );
          }) || crossCoverageSickField[0];
        setComplexCustomFields((prev) => ({
          ...prev,
          'Cross-Coverage-Sick': {
            id: crossCoverageField.id,
            fieldId: crossCoverageField.fieldId,
            value: crossCoverageField.value || '',
            startDate: crossCoverageField.startDate,
            endDate: crossCoverageField.endDate,
            metadata: crossCoverageField.metadata,
            rowId: crossCoverageField.rowId,
          },
        }));
      }

      // Initialize the remaining PCP coverage complex fields
      PCP_COVERAGE_COMPLEX_FIELDS.filter(
        (fieldName) => fieldName !== 'Cross-Coverage-Sick'
      ).forEach((fieldName) => {
        const rows = customFieldsData[fieldName];
        if (Array.isArray(rows) && rows.length > 0) {
          const row =
            rows.find((r) => {
              const v = typeof r.value === 'string' ? r.value.trim() : '';
              const ct = r.metadata?.coverageType as string | undefined;
              const freeText = r.metadata?.freeTextInstructions;
              return (
                (v !== '' && v !== 'None') ||
                Boolean(ct && COVERAGE_LABELS[ct]) ||
                (Array.isArray(r.metadata?.providers) &&
                  (r.metadata.providers as unknown[]).length > 0) ||
                (Array.isArray(r.metadata?.practices) &&
                  (r.metadata.practices as unknown[]).length > 0) ||
                (typeof freeText === 'string' && freeText.trim() !== '')
              );
            }) || rows[0];
          setComplexCustomFields((prev) => ({
            ...prev,
            [fieldName]: {
              id: row.id,
              fieldId: row.fieldId,
              value: row.value || '',
              startDate: row.startDate,
              endDate: row.endDate,
              metadata: row.metadata,
              rowId: row.rowId,
            },
          }));
        }
      });

      // Initialize Cross-Coverage complex field
      const crossCoverageField = customFieldsData['Cross-Coverage'];
      if (
        Array.isArray(crossCoverageField) &&
        crossCoverageField.length > 0
      ) {
        const crossCoverageFieldValue =
          crossCoverageField.find(
            (r) => typeof r.value === 'string' && r.value.trim() !== ''
          ) || crossCoverageField[0];
        setComplexCustomFields((prev) => ({
          ...prev,
          'Cross-Coverage': {
            id: crossCoverageFieldValue.id,
            fieldId: crossCoverageFieldValue.fieldId,
            value: crossCoverageFieldValue.value || '',
            startDate: crossCoverageFieldValue.startDate,
            endDate: crossCoverageFieldValue.endDate,
            metadata: crossCoverageFieldValue.metadata,
            rowId: crossCoverageFieldValue.rowId,
          },
        }));
      }

      // Initialize Supervision-Physician complex field
      const supervisionPhysicianField =
        customFieldsData['Supervision-Physician'];
      if (
        Array.isArray(supervisionPhysicianField) &&
        supervisionPhysicianField.length > 0
      ) {
        const supervisionField = supervisionPhysicianField[0];
        setComplexCustomFields((prev) => ({
          ...prev,
          'Supervision-Physician': {
            id: supervisionField.id,
            fieldId: supervisionField.fieldId,
            value: supervisionField.value || '',
            startDate: supervisionField.startDate,
            endDate: supervisionField.endDate,
            metadata: supervisionField.metadata,
            rowId: supervisionField.rowId,
          },
        }));
      }
    }
  }, [isEditMode, providerData]);

  // Set form field values when complex custom fields are initialized for editing
  useEffect(() => {
    if (
      isEditMode &&
      providerData &&
      Object.keys(complexCustomFields).length > 0
    ) {
      // Set Medicare-Wellness field value
      if (complexCustomFields['Medicare-Wellness']) {
        setValue(
          'Medicare-Wellness',
          complexCustomFields['Medicare-Wellness'],
          {
            shouldDirty: false,
            shouldTouch: false,
          }
        );
      }

      // Set Cross-Coverage-Sick field value
      if (complexCustomFields['Cross-Coverage-Sick']) {
        setValue(
          'Cross-Coverage-Sick',
          complexCustomFields['Cross-Coverage-Sick'],
          {
            shouldDirty: false,
            shouldTouch: false,
          }
        );
      }

      // Set the remaining PCP coverage field values
      PCP_COVERAGE_COMPLEX_FIELDS.filter(
        (fieldName) => fieldName !== 'Cross-Coverage-Sick'
      ).forEach((fieldName) => {
        if (complexCustomFields[fieldName]) {
          setValue(fieldName, complexCustomFields[fieldName], {
            shouldDirty: false,
            shouldTouch: false,
          });
        }
      });

      // Set Cross-Coverage field value
      if (complexCustomFields['Cross-Coverage']) {
        setValue(
          'Cross-Coverage',
          complexCustomFields['Cross-Coverage'],
          {
            shouldDirty: false,
            shouldTouch: false,
          }
        );
      }

      // Set Supervision-Physician field value
      if (complexCustomFields['Supervision-Physician']) {
        setValue(
          'Supervision-Physician',
          complexCustomFields['Supervision-Physician'],
          {
            shouldDirty: false,
            shouldTouch: false,
          }
        );
      }
    }
  }, [isEditMode, providerData, complexCustomFields, setValue]);

  // Get clientId for custom fields - use state/effect to handle empty clientId
  const [copyInsuranceDialog, setCopyInsuranceDialog] = useState<{
    isOpen: boolean;
    newPracticeId: string | null;
    practicesWithInsurance: Array<{ id: string; name: string; planCount: number }>;
  }>({
    isOpen: false,
    newPracticeId: null,
    practicesWithInsurance: [],
  });
  const [selectedSourcePracticeId, setSelectedSourcePracticeId] = useState<string | null>(null);
  const [isCopyingInsurance, setIsCopyingInsurance] = useState(false);
  const [isFetchingPlans, setIsFetchingPlans] = useState(false);
  const [showInsuranceSetup, setShowInsuranceSetup] = useState(false);
  const [newProviderSetupData, setNewProviderSetupData] = useState<{
    providerId: string;
    practiceId: string;
    practiceName: string;
    providerName: string;
  } | null>(null);

  const [clientId, setClientId] = useState<string | null>(() => {
    const id = getClientIdWithFallback();
    return id && id.trim() !== '' ? id : null;
  });

  // Retry getting clientId if it's not available initially
  useEffect(() => {
    if (!clientId) {
      const checkClientId = () => {
        const id = getClientIdWithFallback();
        if (id && id.trim() !== '') {
          setClientId(id);
        }
      };

      // Check immediately
      checkClientId();

      // Retry after a short delay if still not available
      const retryTimer = setTimeout(() => {
        checkClientId();
      }, 500);

      // Retry again after longer delay
      const retryTimer2 = setTimeout(() => {
        checkClientId();
      }, 2000);

      return () => {
        clearTimeout(retryTimer);
        clearTimeout(retryTimer2);
      };
    }
  }, [clientId]);

  // Use custom hook to fetch custom fields
  const {
    customFields: customFieldsFromHook,
    loading: customFieldsLoading,
  } = useCustomFields(clientId, 'ProviderData');

  useEffect(() => {
    if (customFieldsFromHook.length > 0) {
      setCustomFields(customFieldsFromHook);
      setLoading(false);
    }
  }, [customFieldsFromHook]);

  useEffect(() => {
    if (customFieldsLoading) {
      setLoading(true);
    }
  }, [customFieldsLoading]);

  useEffect(() => {
    if (!customFields.length) return;

    const EXCLUDED_FIELDS = [
      'Office-Notes',
      'Daily-Alert',
      'Minimum-Age',
      'Maximum-Age',
      'Cerner-location',
      'Cerner-pool',
      'Credentials', // Exclude from regular processing, handled specially
    ]; // Add more fields here

    const shouldShowField = (name: string) => {
      if (EXCLUDED_FIELDS.includes(name)) return false; // Exclude fields dynamically

      if (
        PCP_ONLY_FIELDS.includes(name) &&
        SPECIALIST_ONLY_FIELDS.includes(name)
      )
        return true;
      if (PCP_ONLY_FIELDS.includes(name))
        return providerType === 'Pcp';
      if (SPECIALIST_ONLY_FIELDS.includes(name))
        return providerType === 'Specialist';
      return true;
    };

    // Option values that are only valid for Specialist providers.
    // These are filtered out of the dropdown for all other provider types.
    const SPECIALIST_ONLY_OPTION_VALUES: Record<string, string[]> = {
      'Workers-Comp': ['VWR'],
    };

    const left: FieldConfig[] = [];
    const right: FieldConfig[] = [];

    customFields.forEach((field) => {
      if (field.name === 'Provider-Type') return; // skip, shown at top
      if (field.name === 'New-Telehealth') return; // skip, handled specially
      if (field.name === 'Out-Of-State-Telehealth') return; // skip, handled specially
      if (field.name === 'Telehealth') return; // skip, handled specially
      if (field.name === 'Next-In-Line') return; // skip, handled specially (PCP only)
      if (field.name === 'Takes-Walk-Ins') return; // skip, handled specially (PCP only)
      if (!shouldShowField(field.name)) {
        return;
      }

      const label = getProviderCustomFieldLabel(field.name);

      const isYesNo = field.allowed_values?.length
        ? field.allowed_values
            .filter((v) => v.value && v.value.trim() !== '') // Filter out empty values
            .every((v) => {
              const nv = String(v.value).trim().toLowerCase();
              const isYesNoValue = nv === 'yes' || nv === 'no';
              return isYesNoValue;
            })
        : false;

      // Special handling for Workers-Comp - force to left column as dropdown
      if (field.name === 'Workers-Comp') {
        const specialistOnly = SPECIALIST_ONLY_OPTION_VALUES[
          'Workers-Comp'
        ].map((v) => v.trim().toLowerCase());
        const options = getOptionsFromAllowedValues(
          field.allowed_values ?? []
        ).filter(
          (opt) =>
            providerType === 'Specialist' ||
            !specialistOnly.includes(opt.value.trim().toLowerCase())
        );
        left.push({
          label: 'Workers Comp',
          name: field.name,
          type: 'select',
          options,
        });
        return;
      }

      // Special handling for multiline text fields - render as textarea
      const multilineFields = [
        'Conditions-Treated',
        'Conditions-Not-Treated',
        'Additional-Location-Days',
        'Additional-Treatments-Offered',
      ];
      if (multilineFields.includes(field.name)) {
        left.push({
          label,
          name: field.name,
          type: 'textarea',
        });
        return;
      }

      // Special handling for Takes-Walk-Ins - exclude from regular processing
      if (field.name === 'Takes-Walk-Ins') {
        // This will be handled specially in the form
        return;
      }

      // Special handling for the PCP coverage fields - excluded from regular
      // processing, they render in the dedicated "Scheduling & Coverage" section
      if (PCP_COVERAGE_COMPLEX_FIELDS.includes(field.name)) {
        return;
      }

      // Special handling for Cross Coverage - exclude from regular processing (Specialist only)
      if (field.name === 'Cross-Coverage') {
        return;
      }

      // Special handling for Supervision-Physician - exclude from regular processing
      if (field.name === 'Supervision-Physician') {
        return;
      }

      // Special handling for Triage-Triggers - exclude from regular processing (Specialist only, shown outside columns)
      if (field.name === 'Triage-Triggers') {
        return;
      }

      if (isYesNo) {
        right.push({ label, name: field.name });
      } else if (field.allowed_values?.length && !isYesNo) {
        const options = field.allowed_values
          .map((v) => ({
            label: v.value === '' ? 'None' : v.value,
            value: v.value,
          }))
          .sort((a, b) => {
            // Sort empty values first, then alphabetically
            if (a.value === '') return -1;
            if (b.value === '') return 1;
            return a.label.localeCompare(b.label);
          });
        left.push({
          label,
          name: field.name,
          type: 'select',
          options,
        });
      } else {
        left.push({ label, name: field.name, type: 'input' });
      }
    });
    setLeftColumnFields(left);
    setRightColumnFields(right);
  }, [customFields, providerType]);

  const isObj = (v: unknown): v is Record<string, unknown> =>
    !!v && typeof v === 'object' && !Array.isArray(v);

  // Rich text rows keep the stored metadata and only refresh htmlContent.
  // When the editor is emptied the stale htmlContent must be dropped.
  const buildRichTextMetadata = (
    existingMetadata: CustomFieldValue['metadata'] | null,
    html: string
  ): CustomFieldValue['metadata'] => {
    const metadata = {
      ...(existingMetadata ?? {}),
    } as CustomFieldValue['metadata'];
    // Editors emit markup like <p><br></p> when emptied, so decide on
    // the visible text rather than the raw string
    if (getVisibleText(html) !== '') {
      metadata.htmlContent = html;
    } else {
      delete metadata.htmlContent;
    }
    return metadata;
  };

  const transformProviderData = (
    formData: Record<string, FormDataObject>,
    customFieldsList: CustomField[],
    providerData?: Provider,
    isEditMode: boolean = false
  ) => {
    const transformedData: Record<string, unknown> = {};

    const rootFields: Array<keyof Provider> = [
      'npi',
      'firstname',
      'middlename',
      'lastname',
      'suffix',
      'title',
      'onPac',
      'nonPac',
      'nickname',
      'pronunciation',
    ];

    // In edit mode only send root fields the user actually changed
    const shouldSendRootField = (
      field: keyof Provider,
      value: unknown
    ) =>
      !isEditMode ||
      !providerData ||
      !isRootFieldUnchanged(value, providerData[field]);

    rootFields.forEach((field) => {
      if (
        formData[field] !== undefined &&
        shouldSendRootField(field, formData[field])
      ) {
        transformedData[field] = formData[field];
      }
    });

    const specialtyField = formData.specialty;
    let specialtyValue: string;
    if (Array.isArray(specialtyField)) {
      specialtyValue = specialtyField
        .filter(
          (value): value is string =>
            typeof value === 'string' && value.trim().length > 0
        )
        .join(', ');
    } else if (typeof specialtyField === 'string') {
      specialtyValue = specialtyField;
    } else {
      specialtyValue = '';
    }
    if (shouldSendRootField('specialty', specialtyValue)) {
      transformedData.specialty = specialtyValue;
    }

    // Set onPac based on Provider-Status
    const providerStatus = formData[
      'Provider-Status'
    ] as unknown as string;
    const onPacValue = providerStatus === 'Active on PAC';
    // In edit mode never derive onPac from a Provider-Status the form does
    // not hold, it would overwrite the stored status with false
    if (
      (!isEditMode || providerStatus !== undefined) &&
      shouldSendRootField('onPac', onPacValue)
    ) {
      transformedData.onPac = onPacValue;
    }

    const id = providerData?.id;
    transformedData.id = id;

    const customFields: Record<string, CustomFieldValue[]> = {};

    // Get current provider type from form data
    const currentProviderType = (formData['Provider-Type'] as string)
      ?.toLowerCase()
      .trim();

    Object.keys(formData).forEach((fieldKey) => {
      const matchedCustomField = customFieldsList.find(
        (cf) => cf.name === fieldKey
      );

      // Skip Resident field as it causes backend validation errors
      if (fieldKey === 'Resident') {
        return;
      }

      // Skip Daily-Alert and Office-Notes as they are managed through card components, not the form
      if (fieldKey === 'Daily-Alert' || fieldKey === 'Office-Notes') {
        return;
      }

      // Only process custom fields that are relevant to the current provider type
      if (matchedCustomField) {
        // Check if this field should be shown for the current provider type
        const shouldShowField = (name: string) => {
          if (
            PCP_ONLY_FIELDS.includes(name) &&
            SPECIALIST_ONLY_FIELDS.includes(name)
          )
            return true;
          if (PCP_ONLY_FIELDS.includes(name))
            return currentProviderType === 'pcp';
          if (SPECIALIST_ONLY_FIELDS.includes(name))
            return currentProviderType === 'specialist';
          return true;
        };

        // Skip fields that don't belong to the current provider type
        if (!shouldShowField(fieldKey)) {
          return;
        }

        let value = formData[fieldKey];
        // Text input normalization: strip placeholder junk values
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed === '') {
            value = '' as unknown as FormDataObject;
          }
        }
        const isEmpty =
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0);

        // Define form fields that should always be sent, even when empty
        const FORM_FIELDS = [
          // PCP Only Fields
          'Unvaccinated-Children',
          'ADD-ADHD',
          'Chronic-Pain-Management',
          'Pap',
          'OB-Visits',
          'Disability-Physical',
          'Military-Physical',
          'Flight-Physical',
          'DOT-Physical',
          'Immigration-Physical',
          'New-Telehealth',
          'Takes-Walk-Ins',
          'Next-In-Line',
          'Additional-Location-Days',
          'Medicare-Wellness',
          'Additional-Treatments-Offered',
          'Type-Of-Schedule',
          'Cross-Coverage-Sick',
          'Cross-Coverage-for-Sick-Market',
          'Closest-Urgent-Care',

          // Specialist Only Fields
          'Provider-Referral-Required',
          'HMO-LT3D-Request',
          'Auto-Accident-Related-Injuries',
          'Same-Day',
          'Virtual-Waiting-Room',
          'Procedures-Surgeries',
          'Conditions-Treated',
          'Conditions-Not-Treated',
          'Cross-Coverage',
          'Telehealth',
          'Triage-Triggers',
          'Facilities-Used',
          'Patients-Virtual-Waiting-Schedule',
          'Scheduling-Notes',

          // Both Provider Types
          'Age',
          'New-Patient',
          'Other-Languages',
          'Out-Of-State-Telehealth',
          'Workers-Comp',
          'Supervision-Physician',
          'State-Specific-Limitations',
          'Provider-Group',
          'Cerner-location',
          'Cerner-pool',
          'Hospital-Backline',
          'Cross-Schedule-Hospital-Follow-Up',

          // Additional Form Fields (not in providerCustomFields.ts but needed for form)
          'Department',
          'Gender',
          'Minimum-Age',
          'Maximum-Age',
          'Provider-Type',
          'Athena-ID',
          'Credentials',
        ];

        let existingRowId: string | null = null;
        let existingId: string | undefined = undefined;
        let existingFieldId: string | undefined = undefined;
        let existingStartDate: string | null = null;
        let existingEndDate: string | null = null;
        let existingMetadata: CustomFieldValue['metadata'] | null =
          null;

        if (
          isEditMode &&
          providerData &&
          providerData.customFields &&
          providerData.customFields[fieldKey] &&
          Array.isArray(providerData.customFields[fieldKey])
        ) {
          const existingCustomField = (
            providerData.customFields[fieldKey] as CustomFieldValue[]
          )[0];
          existingRowId = existingCustomField?.rowId ?? null;
          existingId = existingCustomField?.id;
          existingFieldId = existingCustomField?.fieldId;
          existingStartDate = existingCustomField?.startDate ?? null;
          existingEndDate = existingCustomField?.endDate ?? null;
        }

        // Skip empty values only for non-form fields
        // This prevents sending empty fields that aren't part of our form while allowing form fields to be cleared
        if (isEmpty && !FORM_FIELDS.includes(fieldKey)) {
          return;
        }

        // Ensure FORM_FIELDS are always processed, even if they don't exist in formData
        if (
          FORM_FIELDS.includes(fieldKey) &&
          !(fieldKey in formData)
        ) {
          // Add empty value for FORM_FIELDS that weren't in the form
          const resolvedFieldId =
            matchedCustomField.id ?? existingFieldId;
          if (!resolvedFieldId) {
            return;
          }
          customFields[fieldKey] = [
            {
              id: existingId,
              fieldId: resolvedFieldId,
              value: '',
              startDate: null,
              endDate: null,
              metadata: {},
              rowId: existingRowId,
            },
          ];
          return;
        }

        if (
          isEditMode &&
          providerData &&
          providerData.customFields &&
          providerData.customFields[fieldKey] &&
          Array.isArray(providerData.customFields[fieldKey])
        ) {
          const existingCustomField = (
            providerData.customFields[fieldKey] as CustomFieldValue[]
          )[0];
          existingRowId = existingCustomField?.rowId ?? null;
          existingId = existingCustomField?.id;
          existingFieldId = existingCustomField?.fieldId;
          existingStartDate = existingCustomField?.startDate ?? null;
          existingEndDate = existingCustomField?.endDate ?? null;
          existingMetadata = existingCustomField?.metadata ?? null;
        }

        // Keep the stored metadata as-is in edit mode (null must stay
        // null) so untouched rows are not rewritten on save
        const carriedMetadata = (
          isEditMode ? existingMetadata : {}
        ) as CustomFieldValue['metadata'];

        // Special-case handling ladder for complex fields
        if (fieldKey === 'Medicare-Wellness') {
          // Use the complex object from complexCustomFields if available
          const complexField =
            complexCustomFields['Medicare-Wellness'];
          if (complexField) {
            const resolvedFieldId =
              complexField.fieldId ||
              matchedCustomField.id ||
              existingFieldId;
            if (!resolvedFieldId) {
              return;
            }
            customFields[fieldKey] = [
              {
                id: complexField.id || existingId,
                fieldId: resolvedFieldId,
                value: complexField.value || '',
                startDate:
                  complexField.startDate || existingStartDate || null,
                endDate:
                  complexField.endDate || existingEndDate || null,
                metadata: complexField.metadata || {},
                rowId: complexField.rowId || existingRowId,
              },
            ];
          } else {
            // Fallback to simple string value
            const resolvedFieldId =
              matchedCustomField.id || existingFieldId;
            if (!resolvedFieldId) {
              return;
            }
            customFields[fieldKey] = [
              {
                id: existingId,
                fieldId: resolvedFieldId,
                value: String(value || ''),
                startDate: existingStartDate,
                endDate: existingEndDate,
                metadata: carriedMetadata,
                rowId: existingRowId,
              },
            ];
          }
        } else if (PCP_COVERAGE_COMPLEX_FIELDS.includes(fieldKey)) {
          // Use the complex object from complexCustomFields if available.
          // When the control is never touched, complexCustomFields has no entry
          // but the form value is already the default CustomFieldValue object -
          // use it so we don't stringify it into "[object Object]".
          const untouchedComplexValue =
            value && typeof value === 'object' && !Array.isArray(value)
              ? (value as CustomFieldValue)
              : undefined;
          const complexField =
            complexCustomFields[fieldKey] || untouchedComplexValue;
          if (complexField) {
            const resolvedFieldId =
              complexField.fieldId ||
              matchedCustomField.id ||
              existingFieldId;
            if (!resolvedFieldId) {
              return;
            }
            customFields[fieldKey] = [
              {
                id: complexField.id || existingId,
                fieldId: resolvedFieldId,
                value: complexField.value || '',
                startDate:
                  complexField.startDate || existingStartDate || null,
                endDate:
                  complexField.endDate || existingEndDate || null,
                metadata: complexField.metadata || {},
                rowId: complexField.rowId || existingRowId,
              },
            ];
          } else {
            // Fallback to simple string value
            const resolvedFieldId =
              matchedCustomField.id || existingFieldId;
            if (!resolvedFieldId) {
              return;
            }
            customFields[fieldKey] = [
              {
                id: existingId,
                fieldId: resolvedFieldId,
                value: String(value || ''),
                startDate: existingStartDate,
                endDate: existingEndDate,
                metadata: carriedMetadata,
                rowId: existingRowId,
              },
            ];
          }
        } else if (fieldKey === 'Cross-Coverage') {
          // Use the complex object from complexCustomFields if available
          const complexField = complexCustomFields['Cross-Coverage'];
          if (complexField) {
            const resolvedFieldId =
              complexField.fieldId ||
              matchedCustomField.id ||
              existingFieldId;
            if (!resolvedFieldId) {
              return;
            }
            const finalValue = String(
              complexField.value || ''
            ).trim();
            const metadata =
              finalValue.toLowerCase() === 'none'
                ? {}
                : complexField.metadata || {};
            customFields[fieldKey] = [
              {
                id: complexField.id || existingId,
                fieldId: resolvedFieldId,
                value: complexField.value || '',
                startDate:
                  complexField.startDate || existingStartDate || null,
                endDate:
                  complexField.endDate || existingEndDate || null,
                metadata,
                rowId: complexField.rowId || existingRowId,
              },
            ];
          } else {
            // Fallback to simple string value
            const resolvedFieldId =
              matchedCustomField.id || existingFieldId;
            if (!resolvedFieldId) {
              return;
            }
            customFields[fieldKey] = [
              {
                id: existingId,
                fieldId: resolvedFieldId,
                value: String(value || ''),
                startDate: existingStartDate,
                endDate: existingEndDate,
                metadata: carriedMetadata,
                rowId: existingRowId,
              },
            ];
          }
        } else if (fieldKey === 'Supervision-Physician') {
          // Use the complex object from complexCustomFields if available
          const complexField =
            complexCustomFields['Supervision-Physician'];
          if (complexField) {
            const resolvedFieldId =
              complexField.fieldId ||
              matchedCustomField.id ||
              existingFieldId;
            if (!resolvedFieldId) {
              return;
            }
            customFields[fieldKey] = [
              {
                id: complexField.id || existingId,
                fieldId: resolvedFieldId,
                value: complexField.value || '',
                startDate:
                  complexField.startDate || existingStartDate || null,
                endDate:
                  complexField.endDate || existingEndDate || null,
                metadata: complexField.metadata || {},
                rowId: complexField.rowId || existingRowId,
              },
            ];
          } else {
            // Fallback to simple string value
            const resolvedFieldId =
              matchedCustomField.id || existingFieldId;
            if (!resolvedFieldId) {
              return;
            }
            customFields[fieldKey] = [
              {
                id: existingId,
                fieldId: resolvedFieldId,
                value: String(value || ''),
                startDate: existingStartDate,
                endDate: existingEndDate,
                metadata: carriedMetadata,
                rowId: existingRowId,
              },
            ];
          }
        } else if (fieldKey === 'Scheduling-Notes') {
          // Handle Scheduling-Notes as a rich text field
          const resolvedFieldId =
            matchedCustomField.id ?? existingFieldId;
          if (!resolvedFieldId) {
            return;
          }
          const htmlVal = String(value || '');
          customFields[fieldKey] = [
            {
              id: existingId,
              fieldId: resolvedFieldId,
              value: getVisibleText(htmlVal),
              startDate: existingStartDate,
              endDate: existingEndDate,
              metadata: buildRichTextMetadata(
                carriedMetadata,
                htmlVal
              ),
              rowId: existingRowId,
            },
          ];
        } else if (fieldKey === 'New-Telehealth' && isObj(value)) {
          const ntVal = value as CustomFieldValue;
          const resolvedFieldId =
            ntVal.fieldId ?? matchedCustomField.id ?? existingFieldId;
          if (!resolvedFieldId) {
            return;
          }
          customFields[fieldKey] = [
            {
              id: ntVal.id ?? existingId,
              fieldId: resolvedFieldId,
              value: ntVal.value ?? '',
              startDate: ntVal.startDate ?? existingStartDate ?? null,
              endDate: ntVal.endDate ?? existingEndDate ?? null,
              metadata: ntVal.metadata ?? {},
              rowId: ntVal.rowId ?? existingRowId,
            },
          ];
        } else if (
          fieldKey === 'Out-Of-State-Telehealth' &&
          isObj(value)
        ) {
          const ostVal = value as CustomFieldValue;
          const resolvedFieldId =
            ostVal.fieldId ??
            matchedCustomField.id ??
            existingFieldId;
          if (!resolvedFieldId) {
            return;
          }
          customFields[fieldKey] = [
            {
              id: ostVal.id ?? existingId,
              fieldId: resolvedFieldId,
              value: ostVal.value ?? '',
              startDate:
                ostVal.startDate ?? existingStartDate ?? null,
              endDate: ostVal.endDate ?? existingEndDate ?? null,
              metadata: ostVal.metadata ?? {},
              rowId: ostVal.rowId ?? existingRowId,
            },
          ];
        } else if (fieldKey === 'Telehealth' && isObj(value)) {
          const teleVal = value as CustomFieldValue;
          const resolvedFieldId =
            teleVal.fieldId ??
            matchedCustomField.id ??
            existingFieldId;
          if (!resolvedFieldId) {
            return;
          }
          customFields[fieldKey] = [
            {
              id: teleVal.id ?? existingId,
              fieldId: resolvedFieldId,
              value: teleVal.value ?? '',
              startDate:
                teleVal.startDate ?? existingStartDate ?? null,
              endDate: teleVal.endDate ?? existingEndDate ?? null,
              metadata: teleVal.metadata ?? {},
              rowId: teleVal.rowId ?? existingRowId,
            },
          ];
        } else if (fieldKey === 'Credentials') {
          // Handle Credentials as a regular custom field
          const finalValue =
            value === undefined || value === null
              ? ''
              : String(value);
          const resolvedFieldId =
            matchedCustomField.id ?? existingFieldId;
          if (!resolvedFieldId) {
            return;
          }
          // Always include Credentials field, even if empty
          customFields[fieldKey] = [
            {
              id: existingId, // Include id for edit
              fieldId: resolvedFieldId,
              value: finalValue,
              startDate: existingStartDate,
              endDate: existingEndDate,
              metadata: carriedMetadata,
              rowId: existingRowId,
            },
          ];
        } else {
          // Handle regular custom fields (only include non-empty values)
          const finalValue =
            value === undefined || value === null
              ? ''
              : String(value);
          const resolvedFieldId =
            matchedCustomField.id ?? existingFieldId;
          if (!resolvedFieldId) {
            return;
          }
          const isRichText = RICH_TEXT_FIELDS.includes(
            fieldKey as (typeof RICH_TEXT_FIELDS)[number]
          );
          customFields[fieldKey] = [
            {
              id: existingId,
              fieldId: resolvedFieldId,
              value: isRichText
                ? getVisibleText(finalValue)
                : finalValue,
              startDate: existingStartDate,
              endDate: existingEndDate,
              metadata: isRichText
                ? buildRichTextMetadata(carriedMetadata, finalValue)
                : carriedMetadata,
              rowId: existingRowId,
            },
          ];
        }
      }
    });

    // In edit mode only send custom fields the user actually changed
    transformedData.customFields = isEditMode
      ? pruneUnchangedCustomFields(
          customFields,
          providerData?.customFields,
          {
            isRichTextField: (fieldKey) =>
              RICH_TEXT_FIELDS.includes(
                fieldKey as (typeof RICH_TEXT_FIELDS)[number]
              ),
          }
        )
      : customFields;

    if (
      Array.isArray(providerBridge) &&
      providerBridge.length > 0 &&
      providerBridge[0].practiceId
    ) {
      transformedData.practiceId = providerBridge[0].practiceId;
    }

    // Include providerBridge in the payload
    if (Array.isArray(providerBridge) && providerBridge.length > 0) {
      transformedData.providerBridge = providerBridge.map((item) => {
        // Build metadata object from provider bridge data
        const metadata: { [key: string]: string } = {};
        if (item.metadata) {
          if (
            item.metadata['Athena-Department'] &&
            item.metadata['Athena-Department'].trim() !== ''
          ) {
            metadata['Athena-Department'] =
              item.metadata['Athena-Department'].trim();
          }
          if (
            item.metadata['Athena-Department-Id'] &&
            item.metadata['Athena-Department-Id'].trim() !== ''
          ) {
            metadata['Athena-Department-Id'] =
              item.metadata['Athena-Department-Id'].trim();
          }
          if (
            item.metadata['Athena-Tablespace'] &&
            item.metadata['Athena-Tablespace'].trim() !== ''
          ) {
            metadata['Athena-Tablespace'] =
              item.metadata['Athena-Tablespace'].trim();
          }
          if (
            item.metadata['Athena-Link'] &&
            item.metadata['Athena-Link'].trim() !== ''
          ) {
            metadata['Athena-Link'] =
              item.metadata['Athena-Link'].trim();
          }
        }

        return {
          id: item.id, // Include ID for existing items
          practiceId: item.practiceId,
          startDate: item.startDate || null,
          endDate: item.endDate || null,
          onPac: item.onPac,
          hospitalFollowupPac: item.hospitalFollowupPac ?? null,
          sickCrossCoveragePac: item.sickCrossCoveragePac ?? null,
          providerId: item.providerId, // Include providerId if available
          metadata:
            Object.keys(metadata).length > 0 ? metadata : undefined,
        };
      });
    }

    return transformedData;
  };

  const filterUnsupportedFields = (
    formData: Record<string, FormDataObject>
  ) => {
    const omittedFields = [
      'athenaScheduleName',
      'username',
      'Resident',
    ];
    return Object.keys(formData).reduce((filteredData, key) => {
      if (!omittedFields.includes(key)) {
        filteredData[key] = formData[key];
      }
      return filteredData;
    }, {} as Record<string, FormDataObject>);
  };

  const onSubmit = async (
    formData: Record<string, FormDataObject>
  ) => {
    // Use a temporary variable to store the string[] for relatedPractices
    const relatedPractices = selectedPractices.map((p) => p.value);
    formData.relatedPractices =
      relatedPractices as unknown as FormDataObject;

    const filteredData = filterUnsupportedFields(formData);
    const transformedData = transformProviderData(
      filteredData,
      customFields,
      providerData || undefined,
      isEditMode
    );

    // Block create (not edit) when no practice has been attached
    if (!isEditMode && providerBridge.length === 0) {
      setPracticeError(true);
      toast({
        title: 'Practice required',
        description:
          'Please search for and add at least one practice before saving this provider.',
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
      const practiceSection = document.getElementById(
        'practice-search-section'
      );
      practiceSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      setSaving(true);
      let providerIdResult: string | undefined;
      let response: Awaited<
        ReturnType<typeof createProvider | typeof updateProviderById>
      >;

      if (isEditMode && providerData?.id) {
        response = await updateProviderById(
          providerData.id,
          transformedData
        );
        providerIdResult = providerData.id;
      } else {
        const createResponse = await createProvider(transformedData);
        providerIdResult = createResponse.data?.providerId;
        response = createResponse;
      }

      if (response?.data?.status === 'success' && providerIdResult) {
        // Smooth scroll to top on successful save
        scrollToTop('smooth', 800);

        toast({
          title: isEditMode
            ? 'Provider updated successfully!'
            : 'Provider created successfully!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        // Optimistically hydrate ProviderDetail with updated fields to avoid blanks
        try {
          interface ProviderOptimisticCache {
            [id: string]: Partial<Provider>;
          }
          const w = window as unknown as {
            __providerOptimisticCache?: ProviderOptimisticCache;
          };
          w.__providerOptimisticCache =
            w.__providerOptimisticCache || {};
          w.__providerOptimisticCache[providerIdResult] = {
            ...(providerData || {}),
            id: providerIdResult,
            customFields: {
              ...(providerData?.customFields || {}),
              ...((transformedData.customFields as Record<
                string,
                CustomFieldValue[]
              >) || {}),
            },
          };
        } catch {
          // no-op
        }

        // For new providers, check if we should show insurance setup screen
        if (!isEditMode && providerBridge.length > 0) {
          // Get the first practice from providerBridge (new providers typically have one practice)
          const firstBridgeItem = providerBridge[0];
          const practiceId = firstBridgeItem.practiceId;
          const practiceData = practiceDataMap[practiceId];
          const practiceName = practiceData?.name || firstBridgeItem.tempName || 'Unknown Practice';
          
          // Format provider name
          const providerName = formatProviderName({
            firstName: transformedData.firstname as string,
            lastName: transformedData.lastname as string,
            middleName: transformedData.middlename as string,
            suffix: transformedData.suffix as string,
            title: transformedData.title as string,
          });

          setNewProviderSetupData({
            providerId: providerIdResult,
            practiceId,
            practiceName,
            providerName,
          });
          setShowInsuranceSetup(true);
        } else {
          navigate(`/providers/${providerIdResult}`);
        }
      } else {
        toast({
          title: 'Error',
          description:
            (response?.data as { message?: string })?.message ||
            'Something went wrong',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error: unknown) {
      let errorMsg = 'Something went wrong';
      let isConflict = false;

      if (error instanceof AxiosError && error.response) {
        isConflict = error.response.status === 409;
        const data = error.response.data;
        if (data !== null && typeof data === 'object') {
          if ('message' in data) {
            errorMsg = String((data as { message: unknown }).message);
          } else if ('detail' in data) {
            errorMsg = String((data as { detail: unknown }).detail);
          } else {
            errorMsg = error.message;
          }
        } else if (typeof data === 'string') {
          errorMsg = data;
        } else {
          errorMsg = error.message;
        }
      } else if (
        typeof error === 'object' &&
        error !== null &&
        'message' in error
      ) {
        errorMsg = String((error as { message?: unknown }).message);
      }

      toast({
        title: isConflict ? 'Duplicate provider' : 'Error saving provider',
        description: errorMsg,
        status: 'error',
        duration: isConflict ? 8000 : 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // Initialize complex fields for create mode
  useEffect(() => {
    if (!isEditMode && customFields.length > 0) {
      // Initialize complex fields with proper fieldId for create mode
      const complexFieldsToInitialize = [
        'Medicare-Wellness',
        'New-Telehealth',
        'Telehealth',
        'Additional-Treatments-Offered',
        'Cross-Coverage-Sick',
        'Cross-Coverage-for-Sick-Market',
        'Closest-Urgent-Care',
        'Type-Of-Schedule',
        'Provider-Referral-Required',
        'Out-Of-State-Telehealth',
        'Cross-Coverage',
        'Supervision-Physician',
      ];

      complexFieldsToInitialize.forEach((fieldName) => {
        const customField = customFields.find(
          (cf) => cf.name === fieldName
        );
        if (customField?.id) {
          setComplexCustomFields((prev) => ({
            ...prev,
            [fieldName]: {
              value: '',
              metadata: {},
              id: undefined,
              rowId: null,
              fieldId: customField.id,
              startDate: null,
              endDate: null,
            },
          }));
        }
      });
    }
  }, [isEditMode, customFields]);

  useEffect(() => {
    if (isEditMode && providerData && customFields.length > 0) {
      let initialValues = getInitialProviderFormValues(
        providerData,
        customFields
      );

      // Normalize all Yes/No custom fields to 'Yes'/'No' (handles 'YES'/'NO', booleans, etc.)
      const detectedYesNoFields = customFields
        .filter(
          (cf) =>
            Array.isArray(cf.allowed_values) &&
            cf.allowed_values.length > 0 &&
            cf.allowed_values.every((v) => {
              const nv = String(v.value).trim().toLowerCase();
              return nv === 'yes' || nv === 'no';
            })
        )
        .map((cf) => cf.name);

      initialValues = {
        ...initialValues,
        ...Object.fromEntries(
          detectedYesNoFields.map((key) => {
            const val = initialValues[key];
            if (typeof val === 'string') {
              return [key, normalizeYesNo(val) ?? val];
            }
            return [key, val];
          })
        ),
      };

      reset(initialValues);
    }
  }, [isEditMode, providerData, customFields, reset]);

  // Clear the practice validation error as soon as the user adds a practice
  useEffect(() => {
    if (providerBridge.length > 0) {
      setPracticeError(false);
    }
  }, [providerBridge]);

  useEffect(() => {
    if (isEditMode && providerData?.relatedPractices) {
      setSelectedPractices(
        providerData.relatedPractices.map(
          (practice: { name: string; id: string }) => ({
            label: practice.name,
            value: practice.id,
          })
        )
      );
      // Always initialize providerBridge from backend response
      if (Array.isArray(providerData.providerBridge)) {
        setProviderBridge(
          providerData.providerBridge.map(
            (item: ProviderBridgeItem) => ({
              practiceId: item.practiceId,
              startDate: item.startDate ?? '',
              endDate: item.endDate ?? '',
              onPac: !!item.onPac,
              hospitalFollowupPac: item.hospitalFollowupPac ?? null,
              sickCrossCoveragePac: item.sickCrossCoveragePac ?? null,
              id: item.id,
              providerId: item.providerId,
              metadata: item.metadata,
            })
          )
        );
      }

      // Fetch practice data for all related practices to get externalTablespace
      const fetchPracticeData = async () => {
        const practiceMap: Record<string, Practice> = {};
        if (
          !providerData.relatedPractices ||
          providerData.relatedPractices.length === 0
        ) {
          return;
        }
        await Promise.all(
          providerData.relatedPractices.map(async (practice) => {
            try {
              const { data } = await getPracticeById(practice.id);
              if (data?.practice) {
                practiceMap[practice.id] = data.practice;
              }
            } catch {
              // Ignore errors when fetching practice data
            }
          })
        );
        setPracticeDataMap(practiceMap);

        // Pre-fill tablespace for practices that don't have it set
        setProviderBridge((prevBridge) =>
          prevBridge.map((bridgeItem) => {
            // Skip if tablespace already exists in metadata
            if (bridgeItem.metadata?.['Athena-Tablespace']) {
              return bridgeItem;
            }

            const practice =
              practiceMap[bridgeItem.practiceId] ||
              providerData?.relatedPractices?.find(
                (p) => p.id === bridgeItem.practiceId
              );

            if (practice) {
              const practiceWithTablespace = practice as unknown as {
                externalTablespace?: string;
              };
              if (practiceWithTablespace.externalTablespace) {
                return {
                  ...bridgeItem,
                  metadata: {
                    ...bridgeItem.metadata,
                    'Athena-Tablespace':
                      practiceWithTablespace.externalTablespace,
                  },
                };
              }

              // Try to get from market data
              try {
                const statesData = JSON.parse(
                  localStorage.getItem('statesData') || '[]'
                );
                const practiceMarketId =
                  practice.marketId ||
                  (practice as unknown as { marketid?: string })
                    .marketid;
                if (practiceMarketId) {
                  for (const state of statesData) {
                    const market = state.markets?.find(
                      (m: { id: string; tablespace?: string }) =>
                        m.id === practiceMarketId
                    );
                    if (
                      market?.tablespace &&
                      market.tablespace.trim() !== ''
                    ) {
                      return {
                        ...bridgeItem,
                        metadata: {
                          ...bridgeItem.metadata,
                          'Athena-Tablespace':
                            market.tablespace.trim(),
                        },
                      };
                    }
                  }
                }
              } catch (error) {
                // Ignore errors
              }
            }

            return bridgeItem;
          })
        );
      };

      fetchPracticeData();
    }
  }, [isEditMode, providerData]);

  const handleAddPractice = async (practice: {
    label: string;
    value: string;
  }) => {
    if (
      !providerBridge.find((pb) => pb.practiceId === practice.value)
    ) {
      // Fetch practice data to get externalTablespace first
      let tablespace = '';
      try {
        const { data } = await getPracticeById(practice.value);
        if (data?.practice) {
          setPracticeDataMap((prev) => ({
            ...prev,
            [practice.value]: data.practice,
          }));

          // Get tablespace from practice
          const practiceWithTablespace = data.practice as unknown as {
            externalTablespace?: string;
          };
          if (practiceWithTablespace.externalTablespace) {
            tablespace = practiceWithTablespace.externalTablespace;
          } else {
            // Try to get from market data
            try {
              const statesData = JSON.parse(
                localStorage.getItem('statesData') || '[]'
              );
              const practiceMarketId =
                data.practice.marketId ||
                (data.practice as unknown as { marketid?: string })
                  .marketid;
              if (practiceMarketId) {
                for (const state of statesData) {
                  const market = state.markets?.find(
                    (m: { id: string; tablespace?: string }) =>
                      m.id === practiceMarketId
                  );
                  if (
                    market?.tablespace &&
                    market.tablespace.trim() !== ''
                  ) {
                    tablespace = market.tablespace.trim();
                    break;
                  }
                }
              }
            } catch (error) {
              // Ignore errors
            }
          }
        }
      } catch {
        // Ignore errors
      }

      setProviderBridge([
        ...providerBridge,
        {
          practiceId: practice.value,
          providerId: providerData?.id,
          startDate: '',
          endDate: '',
          onPac: true, // Default value for new items
          id: undefined, // New items won't have an ID
          tempName: practice.label, // Temporarily store the practice name for UI purposes
          metadata: tablespace
            ? {
                'Athena-Tablespace': tablespace,
              }
            : undefined,
        },
      ]);

      // Check if provider has existing insurance in other practices, or fetch plans from newly added practice
      if (providerData?.id) {
        checkForExistingInsurance(providerData.id, practice.value);
      }
    }
  };

  const checkForExistingInsurance = async (
    providerId: string,
    newPracticeId: string
  ) => {
    setIsFetchingPlans(true);
    try {
      // Get all practices this provider is linked to (excluding the new one)
      const existingPractices =
        providerData?.relatedPractices?.filter((p) => p.id !== newPracticeId) || [];

      // If no existing practices, fetch plans from the newly added practice itself
      if (existingPractices.length === 0) {
        try {
          const practiceName = practiceDataMap[newPracticeId]?.name || 
                               providerData?.relatedPractices?.find(p => p.id === newPracticeId)?.name ||
                               'the new practice';
          
          // Fetch plans from the newly added practice (without provider filter to get all plans in that practice)
          const result = await getPlansList(null, {
            practiceIds: [newPracticeId],
          });

          if (result.data?.insurancePlans) {
            // Count unique insurance plans (by insurance.id)
            const uniqueInsuranceIds = new Set<string>();
            for (const insurancePlan of result.data.insurancePlans) {
              for (const plan of insurancePlan.plans) {
                uniqueInsuranceIds.add(plan.insurance.id);
              }
            }
            const planCount = uniqueInsuranceIds.size;

            // If plans found in the newly added practice, show dialog
            if (planCount > 0) {
              setCopyInsuranceDialog({
                isOpen: true,
                newPracticeId,
                practicesWithInsurance: [{
                  id: newPracticeId,
                  name: practiceName,
                  planCount,
                }],
              });
              // Auto-select the newly added practice as source
              setSelectedSourcePracticeId(newPracticeId);
            }
          }
        } catch {
          // Ignore errors when fetching plans from newly added practice
        } finally {
          setIsFetchingPlans(false);
        }
        return;
      }

      // Check which practices have insurance using Promise.allSettled for parallel execution
      // This ensures all practices are checked even if some fail, and errors don't prevent checking others
      const practicesWithInsurance: Array<{ id: string; name: string; planCount: number }> = [];
      
      const practiceCheckResults = await Promise.allSettled(
        existingPractices.map(async (practice) => {
          const plans = await fetchSourceInsurancePlans(providerId, practice.id);
          const planCount = plans.reduce((sum, p) => sum + p.plans.length, 0);
          return {
            practice,
            planCount,
          };
        })
      );

      // Process results - only include practices that successfully returned insurance plans
      for (const result of practiceCheckResults) {
        if (result.status === 'fulfilled' && result.value.planCount > 0) {
          practicesWithInsurance.push({
            id: result.value.practice.id,
            name: result.value.practice.name,
            planCount: result.value.planCount,
          });
        }
        // Ignore rejected promises - individual practice check failures don't prevent showing dialog
      }

      // If practices with insurance found, show dialog automatically
      if (practicesWithInsurance.length > 0) {
        setCopyInsuranceDialog({
          isOpen: true,
          newPracticeId,
          practicesWithInsurance,
        });
        // Auto-select first practice if only one option
        if (practicesWithInsurance.length === 1) {
          setSelectedSourcePracticeId(practicesWithInsurance[0].id);
        }
      }
    } catch {
      // Ignore errors
    } finally {
      setIsFetchingPlans(false);
    }
  };

  const handleCopyInsurance = async () => {
    if (!providerData?.id || !copyInsuranceDialog.newPracticeId || !selectedSourcePracticeId) {
      return;
    }

    setIsCopyingInsurance(true);
    try {
      let sourcePlans;
      
      // If copying from the newly added practice itself (no existing practices scenario),
      // fetch plans from that practice without provider filter
      if (selectedSourcePracticeId === copyInsuranceDialog.newPracticeId) {
        const result = await getPlansList(null, {
          practiceIds: [selectedSourcePracticeId],
        });
        sourcePlans = result.data?.insurancePlans || [];
      } else {
        // Normal case: copy from another practice where this provider has insurance
        sourcePlans = await fetchSourceInsurancePlans(providerData.id, selectedSourcePracticeId);
      }
      
      const gridEntries = prepareGridEntries(
        sourcePlans,
        providerData.id,
        copyInsuranceDialog.newPracticeId
      );

      await createGrid({ gridEntries });

      toast({
        title: 'Insurance copied successfully',
        description: `Copied ${gridEntries.length} insurance plan(s) to the new practice`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setCopyInsuranceDialog({ isOpen: false, newPracticeId: null, practicesWithInsurance: [] });
      setSelectedSourcePracticeId(null);
    } catch (err) {
      toast({
        title: 'Error copying insurance',
        description: err instanceof Error ? err.message : 'Failed to copy insurance plans',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCopyingInsurance(false);
    }
  };

  useEffect(() => {
    if (isEditMode && providerData) {
      const inferred = getProviderTypeFromData(
        providerData.customFields as ProviderCustomFields,
        providerData.specialty
      );
      setValue('Provider-Type', inferred, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [isEditMode, providerData, setValue]);

  // Get practice names for the copy insurance dialog
  const practiceNames = useMemo(() => {
    const newPracticeName = copyInsuranceDialog.newPracticeId
      ? (practiceDataMap[copyInsuranceDialog.newPracticeId]?.name ||
         providerData?.relatedPractices?.find(p => p.id === copyInsuranceDialog.newPracticeId)?.name ||
         'the new practice')
      : 'the new practice';
    
    const selectedSourcePractice = selectedSourcePracticeId
      ? copyInsuranceDialog.practicesWithInsurance.find(p => p.id === selectedSourcePracticeId)
      : copyInsuranceDialog.practicesWithInsurance[0] || null;
    
    return {
      newPracticeName,
      sourcePracticeName: selectedSourcePractice?.name || '',
      sourcePlanCount: selectedSourcePractice?.planCount || 0,
    };
  }, [copyInsuranceDialog.newPracticeId, copyInsuranceDialog.practicesWithInsurance, selectedSourcePracticeId, practiceDataMap, providerData?.relatedPractices]);

  // Show error UI in edit mode if fetch failed
  if (isEditMode && providerError) {
    return (
      <Flex
        direction="column"
        width="60rem"
        p={6}
        mx="auto"
        align="center"
        justify="center"
        minH="50vh"
      >
        <Text
          fontSize="1.5rem"
          fontWeight="600"
          color="red.500"
          mb={4}
        >
          Failed to load provider data
        </Text>
        <Text color="gray.600" mb={6} textAlign="center">
          {providerError.message}
        </Text>
        <Flex gap={4}>
          <Button colorScheme="blue" onClick={fetchProvider}>
            Retry
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              navigate(
                providerId ? `/providers/${providerId}` : '/providers'
              )
            }
          >
            Back
          </Button>
        </Flex>
      </Flex>
    );
  }

  // Show loader in edit mode while fetching data
  if (isEditMode && !providerData) {
    return <PageLoader />;
  }

  if (loading) return <PageLoader />;

  return (
    <>
      <Helmet>
        <title>
          {isEditMode && providerData
            ? `Edit Provider - ${formatProviderName({
                ...providerData,
                customFields: providerData.customFields,
              })}`
            : isEditMode
            ? 'Edit Provider'
            : 'Create Provider'}
        </title>
      </Helmet>
      <Flex direction="column" width="60rem" p={6} mx="auto">
        <Text
          fontSize="1.875rem"
          fontWeight="700"
          lineHeight={'133%'}
          color="text.primary"
          mb="2rem"
        >
          {isEditMode ? 'Edit' : 'Create'} Provider
        </Text>

        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ width: '100%' }}
        >
          <Flex direction="column">
            <FormControl mb="1.5rem" isRequired isInvalid={!!errors['Provider-Type']}>
              <FormLabel>Provider Type</FormLabel>
              <Select
                {...register('Provider-Type', { required: 'Provider Type is required' })}
                placeholder="Select Provider Type"
                width="40%"
                onChange={(e) => {
                  const v = e.target.value;
                  const canon =
                    v.toLowerCase() === 'specialty'
                      ? 'Specialist'
                      : v;
                  setValue(
                    'Provider-Type',
                    canon as CanonProviderType,
                    {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    }
                  );
                }}
              >
                {customFields
                  .find((f) => f.name === 'Provider-Type')
                  ?.allowed_values?.map((opt) => {
                    const canon =
                      opt.value.toLowerCase() === 'specialty'
                        ? 'Specialist'
                        : opt.value;
                    return (
                      <option key={opt.value} value={canon}>
                        {canon}
                      </option>
                    );
                  })}
              </Select>
              <FormErrorMessage>
                {errors['Provider-Type']?.message as string}
              </FormErrorMessage>
            </FormControl>
            <FormControl mb="1.5rem">
              <FormLabel>Specialties</FormLabel>
              <Controller
                name="specialty"
                control={control}
                defaultValue={[]}
                render={({ field }) => {
                  const valueArray = Array.isArray(field.value)
                    ? (field.value as string[])
                    : typeof field.value === 'string'
                    ? field.value
                        .split(',')
                        .map((item) => item.trim())
                        .filter((item) => item.length > 0)
                    : [];

                  const allowedOptions = specialtyAllowedOptions;
                  const selectedOptions = valueArray.map((value) => {
                    const match = allowedOptions.find(
                      (option) =>
                        option.value.toLowerCase() ===
                        value.toLowerCase()
                    );
                    return (
                      match || {
                        label: value,
                        value,
                      }
                    );
                  });

                  const combinedOptions = [
                    ...allowedOptions,
                    ...selectedOptions.filter(
                      (option) =>
                        !allowedOptions.some(
                          (allowed) =>
                            allowed.value.toLowerCase() ===
                            option.value.toLowerCase()
                        )
                    ),
                  ];

                  return (
                    <ReactSelect
                      isMulti
                      options={combinedOptions}
                      value={selectedOptions}
                      onChange={(selected) => {
                        const values = Array.isArray(selected)
                          ? selected.map((option) => option.value)
                          : [];
                        field.onChange(values);
                      }}
                      placeholder="Select specialties"
                      classNamePrefix="react-select"
                      styles={specialtySelectStyles}
                    />
                  );
                }}
              />
              <FormHelperText>
                Choose one or more specialties for this provider.
              </FormHelperText>
            </FormControl>
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              lineHeight={'120%'}
              color="text.secondary"
              mb="1.5rem"
            >
              Identification Details
            </Text>
            <FormControl isInvalid={!!errors.npi} mb="1.5rem">
              <FormLabel>NPI*</FormLabel>
              <Input
                {...register('npi', {
                  required: 'NPI is required',
                  setValueAs: (v: unknown) =>
                    typeof v === 'string' ? v.trim() : v,
                  maxLength: {
                    value: 10,
                    message: 'NPI must be 10 characters or fewer',
                  },
                })}
                placeholder="Add NPI"
              />
              <FormErrorMessage>
                {errors.npi?.message as string}
              </FormErrorMessage>
            </FormControl>
            <Flex gap="1rem" mb="1.5rem">
              <FormControl isInvalid={!!errors.firstname}>
                <FormLabel>First name*</FormLabel>
                <Input
                  {...register('firstname', {
                    required: 'First name is required',
                  })}
                />
                <FormErrorMessage>
                  {errors.firstname?.message as string}
                </FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Middle name</FormLabel>
                <Input {...register('middlename')} />
              </FormControl>

              <FormControl isInvalid={!!errors.lastname}>
                <FormLabel>Last name*</FormLabel>
                <Input
                  {...register('lastname', {
                    required: 'Last name is required',
                  })}
                />
                <FormErrorMessage>
                  {errors.lastname?.message as string}
                </FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Suffix</FormLabel>
                <Controller
                  name="suffix"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} placeholder="Select suffix">
                      {getSuffixOptions().map(({ label, value }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Credentials</FormLabel>
                <Controller
                  name="Credentials"
                  control={control}
                  defaultValue=""
                  render={({ field }) => {
                    const credentialsField = customFields.find(
                      (cf) => cf.name === 'Credentials'
                    );
                    const options =
                      credentialsField?.allowed_values?.map((v) => ({
                        label: v.value === '' ? 'None' : v.value,
                        value: v.value,
                      })) || [];

                    return (
                      <Select
                        {...field}
                        placeholder="Select credentials"
                      >
                        <option value="">None</option>
                        {options
                          .filter((opt) => opt.value !== '')
                          .sort((a, b) =>
                            a.label.localeCompare(b.label)
                          )
                          .map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                      </Select>
                    );
                  }}
                />
              </FormControl>
            </Flex>
            <FormControl isInvalid={!!errors.title} mb="1.5rem">
              <FormLabel>Title</FormLabel>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select title"
                    width="13rem"
                  >
                    {getTitleOptions().map(({ label, value }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                )}
              />
              <FormErrorMessage>
                {errors.title?.message as string}
              </FormErrorMessage>
            </FormControl>
            <FormControl mb="1.5rem">
              <FormLabel>Nickname</FormLabel>
              <Input
                {...register('nickname')}
                placeholder="Enter nickname"
              />
            </FormControl>
            <FormControl mb="1.5rem">
              <FormLabel>Pronunciation</FormLabel>
              <Input
                {...register('pronunciation')}
                placeholder="Enter pronunciation"
              />
            </FormControl>
            <FormControl mb="1.5rem">
              <FormLabel>Gender</FormLabel>
              <Controller
                name="Gender"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RadioGroup {...field} value={field.value || ''}>
                    <HStack spacing="2rem">
                      <Radio value="Male">Male</Radio>
                      <Radio value="Female">Female</Radio>
                      <Radio value="Nonbinary">Nonbinary</Radio>
                    </HStack>
                  </RadioGroup>
                )}
              />
            </FormControl>
            <Text
              fontSize="1rem"
              fontWeight="500"
              lineHeight="120%"
              color="text.primary"
              mb="0.75rem"
            >
              EMR Details
            </Text>
            <Flex gap="1rem" mb="2rem">
              <FormControl>
                <FormLabel>Athena ID</FormLabel>
                <Input
                  {...register('Athena-ID')}
                  placeholder="Enter Athena ID"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Cerner Encounter Location</FormLabel>
                <Input
                  {...register('Cerner-location')}
                  placeholder="Enter Cerner Encounter Location"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Cerner Pool</FormLabel>
                <Input
                  {...register('Cerner-pool')}
                  placeholder="Enter Cerner pool"
                />
              </FormControl>
            </Flex>

            {/* <FormControl mb="1.5rem">
              <FormLabel>Specialty</FormLabel>
              <Select
                {...register('specialty')}
                placeholder="Select specialty"
                width="13rem"
              >
                {SPECIALTY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </FormControl> */}
            {/* Triage Triggers - Specialist only */}
            {providerType === 'Specialist' && (
              <Box mb="1.5rem" width="100%">
                <Text
                  fontSize="1rem"
                  fontWeight="600"
                  color="gray.700"
                  mb="1rem"
                >
                  Triage Triggers
                </Text>
                <Controller
                  name="Triage-Triggers"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <RichTextEditor
                      value={field.value || ''}
                      onChange={(value) => {
                        field.onChange(value);
                        setValue('Triage-Triggers', value, {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                      placeholder="Enter triage triggers"
                      minHeight="6rem"
                      maxHeight="12rem"
                    />
                  )}
                />
              </Box>
            )}
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              lineHeight={'120%'}
              color="text.secondary"
              mb="1rem"
            >
              Treatments & Scheduling
            </Text>
            <Flex gap={6} align="flex-start" width="100%" mb={'2rem'}>
              {/* Left Column – Inputs and Dropdowns */}
              <Box
                flex="1"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                overflow="hidden"
                bg="white"
                boxShadow="sm"
                _hover={{ boxShadow: 'md' }}
                transition="all 0.2s"
              >
                {leftColumnFields
                  .filter(
                    (field) =>
                      field.name !== 'Medicare-Wellness' &&
                      field.name !== 'Provider-Type' &&
                      field.name !== 'specialty' &&
                      field.name !== 'Athena-ID' &&
                      field.name !== 'Triage-Triggers' &&
                      field.name !== 'Cross-Coverage-Sick' &&
                      field.name !==
                        'Cross-Coverage-for-Sick-Market' &&
                      field.name !== 'Closest-Urgent-Care' &&
                      field.name !== 'Cross-Coverage' &&
                      field.name !== 'Supervision-Physician' &&
                      field.name !== 'Scheduling-Notes' &&
                      field.name !== 'Gender' &&
                      field.name !== 'Next-In-Line' &&
                      field.name !== 'Department'
                  )
                  .map(
                    (
                      { label, type = 'input', name, options },
                      idx
                    ) => (
                      <Box
                        key={name}
                        borderBottom={
                          idx !== leftColumnFields.length - 1
                            ? '1px solid'
                            : 'none'
                        }
                        borderColor="gray.100"
                        p="1rem 1.25rem"
                        _hover={{ bg: 'gray.50' }}
                        transition="background 0.2s"
                      >
                        <FormControl>
                          {type === 'textarea' ? (
                            <VStack align="stretch" spacing={2}>
                              <FormLabel
                                fontSize="0.875rem"
                                fontWeight="600"
                                mb="0"
                                color="gray.700"
                              >
                                {label}
                              </FormLabel>
                              <RichTextEditor
                                value={watch(name) || ''}
                                onChange={(value) =>
                                  setValue(name, value, {
                                    shouldDirty: true,
                                  })
                                }
                                placeholder={`Enter ${getProviderFieldPlaceholderLabel(label)}`}
                                minHeight="15rem"
                                maxHeight="40rem"
                              />
                            </VStack>
                          ) : name === 'Cross-Schedule-Hospital-Follow-Up' ? (
                            <VStack align="stretch" spacing={2}>
                              <FormLabel
                                fontSize="0.875rem"
                                fontWeight="600"
                                mb="0"
                                color="gray.700"
                              >
                                {label}
                              </FormLabel>
                              <Textarea
                                {...register(name)}
                                placeholder="Enter instructions for cross-scheduling hospital follow-ups"
                                width="100%"
                                minHeight="6rem"
                                resize="vertical"
                                borderRadius="md"
                                borderColor="gray.300"
                                _hover={{ borderColor: 'gray.400' }}
                                _focus={{
                                  borderColor: 'blue.500',
                                  boxShadow:
                                    '0 0 0 1px var(--chakra-colors-blue-500)',
                                }}
                              />
                            </VStack>
                          ) : (
                            <Flex
                              align="center"
                              justify="space-between"
                              gap={4}
                            >
                              <FormLabel
                                fontSize="0.875rem"
                                fontWeight="600"
                                mb="0"
                                whiteSpace="normal"
                                wordBreak="break-word"
                                minW="160px"
                                color="gray.700"
                              >
                                {label}
                              </FormLabel>
                              {type === 'select' && options ? (
                                <Select
                                  {...register(name)}
                                  placeholder={`Select ${getProviderFieldPlaceholderLabel(label)}`}
                                  width="65%"
                                  height="2rem"
                                  borderRadius="md"
                                  borderColor="gray.300"
                                  _hover={{ borderColor: 'gray.400' }}
                                  _focus={{
                                    borderColor: 'blue.500',
                                    boxShadow:
                                      '0 0 0 1px var(--chakra-colors-blue-500)',
                                  }}
                                  transition="all 0.2s"
                                  onChange={(e) => {
                                    const selectedValue =
                                      e.target.value;
                                    if (selectedValue === '') {
                                      // Reset logic for the field
                                      setValue(name, '');
                                    }
                                  }}
                                >
                                  {options.map((opt, index) => (
                                    <option
                                      key={
                                        opt.value === ''
                                          ? `empty-${index}`
                                          : opt.value
                                      }
                                      value={opt.value}
                                    >
                                      {opt.label}
                                    </option>
                                  ))}
                                </Select>
                              ) : (
                                <Input
                                  {...register(name)}
                                  placeholder={`Enter ${getProviderFieldPlaceholderLabel(label)}`}
                                  width="65%"
                                  height="2rem"
                                  borderRadius="md"
                                  borderColor="gray.300"
                                  _hover={{ borderColor: 'gray.400' }}
                                  _focus={{
                                    borderColor: 'blue.500',
                                    boxShadow:
                                      '0 0 0 1px var(--chakra-colors-blue-500)',
                                  }}
                                  transition="all 0.2s"
                                />
                              )}
                            </Flex>
                          )}
                        </FormControl>
                      </Box>
                    )
                  )}
              </Box>

              {/* Right Column – Yes/No Toggles */}
              <Box
                flex="1"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                overflow="hidden"
                bg="white"
                boxShadow="sm"
                _hover={{ boxShadow: 'md' }}
                transition="all 0.2s"
              >
                {rightColumnFields
                  .filter(
                    (field) =>
                      field.name !== 'Takes-Walk-Ins' &&
                      field.name !== 'Gender' &&
                      field.name !== 'Cross-Coverage-Sick' &&
                      field.name !==
                        'Cross-Coverage-for-Sick-Market' &&
                      field.name !== 'Closest-Urgent-Care' &&
                      field.name !== 'Medicare-Wellness' &&
                      field.name !== 'New-Telehealth' &&
                      field.name !== 'Out-Of-State-Telehealth' &&
                      field.name !== 'Telehealth' &&
                      field.name !== 'Supervision-Physician' &&
                      field.name !== 'Scheduling-Notes' &&
                      field.name !== 'Triage-Triggers' &&
                      field.name !== 'Next-In-Line'
                  )
                  .map(({ label, name }, idx) => (
                    <Box
                      key={name}
                      borderBottom={
                        idx !== rightColumnFields.length - 1
                          ? '1px solid'
                          : 'none'
                      }
                      borderColor="gray.100"
                      p="1rem 1.25rem"
                      _hover={{ bg: 'gray.50' }}
                      transition="background 0.2s"
                    >
                      <FormControl>
                        <Flex align="center" justify="space-between">
                          <FormLabel
                            fontSize="0.875rem"
                            fontWeight="600"
                            mb="0"
                            whiteSpace="nowrap"
                            color="gray.700"
                          >
                            {label}
                          </FormLabel>
                          <Controller
                            name={name}
                            control={control}
                            defaultValue=""
                            render={({ field }) => {
                              const v = String(field.value || '')
                                .trim()
                                .toLowerCase();
                              const isYes = v === 'yes';
                              const isNo = v === 'no';

                              return (
                                <HStack spacing={3}>
                                  <Button
                                    size="sm"
                                    height="2rem"
                                    px={4}
                                    colorScheme={
                                      isYes ? 'green' : 'gray'
                                    }
                                    variant={
                                      isYes ? 'solid' : 'outline'
                                    }
                                    onClick={() =>
                                      field.onChange('Yes')
                                    }
                                    borderRadius="md"
                                    fontWeight="500"
                                  >
                                    Yes
                                  </Button>
                                  <Button
                                    size="sm"
                                    height="2rem"
                                    px={4}
                                    colorScheme={
                                      isNo ? 'red' : 'gray'
                                    }
                                    variant={
                                      isNo ? 'solid' : 'outline'
                                    }
                                    onClick={() =>
                                      field.onChange('No')
                                    }
                                    borderRadius="md"
                                    fontWeight="500"
                                  >
                                    No
                                  </Button>
                                </HStack>
                              );
                            }}
                          />
                        </Flex>
                      </FormControl>
                    </Box>
                  ))}
              </Box>
            </Flex>
            {/* Scheduling Notes - Right side - Specialist only */}
            {providerType === 'Specialist' && (
              <Box mb="1.5rem" width="100%">
                <Text
                  fontSize="1rem"
                  fontWeight="600"
                  color="gray.700"
                  mb="1rem"
                >
                  Scheduling Notes
                </Text>
                <Controller
                  name="Scheduling-Notes"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <RichTextEditor
                      value={field.value || ''}
                      onChange={(value) => {
                        field.onChange(value);
                        setValue('Scheduling-Notes', value, {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                      placeholder="Enter scheduling notes"
                      minHeight="8rem"
                    />
                  )}
                />
              </Box>
            )}
            {providerType === 'Pcp' && (
              <FormFieldErrorBoundary fieldName="Medicare Wellness">
                <Controller
                  name="Medicare-Wellness"
                  control={control}
                  defaultValue={
                    complexCustomFields['Medicare-Wellness'] || {
                      value: '',
                      metadata: {},
                      id: undefined,
                      rowId: null,
                      startDate: null,
                      endDate: null,
                      fieldId:
                        customFields.find(
                          (cf) => cf.name === 'Medicare-Wellness'
                        )?.id || '',
                    }
                  }
                  render={({ field }) => (
                    <MedicareWellnessSelect
                      field={field.value}
                      practices={allPracticesForMedicare}
                      providerBridge={providerBridge}
                      onChange={(updatedField) => {
                        // Ensure fieldId is set if it's missing
                        if (
                          !updatedField.fieldId &&
                          customFields.find(
                            (cf) => cf.name === 'Medicare-Wellness'
                          )?.id
                        ) {
                          updatedField.fieldId =
                            customFields.find(
                              (cf) => cf.name === 'Medicare-Wellness'
                            )?.id || '';
                        }

                        // Store the complex object in complexCustomFields for later use in transformProviderData
                        setComplexCustomFields((prev) => ({
                          ...prev,
                          'Medicare-Wellness': updatedField,
                        }));

                        // Pass the full field object to maintain component state
                        field.onChange(updatedField);
                      }}
                    />
                  )}
                />
              </FormFieldErrorBoundary>
            )}
            {/* New Telehealth Field - All provider types (PCP & Specialist) */}
            <FormControl mb="1.5rem">
              <FormLabel>New Telehealth</FormLabel>
              <Controller
                name="New-Telehealth"
                  control={control}
                  defaultValue={
                    complexCustomFields['New-Telehealth'] || {
                      value: '',
                      metadata: {},
                      id: undefined,
                      rowId: null,
                      fieldId:
                        customFields.find(
                          (cf) => cf.name === 'New-Telehealth'
                        )?.id || '',
                      startDate: null,
                      endDate: null,
                    }
                  }
                  render={({ field }) => {
                    const currentValue = field.value?.value || '';
                    const isYes =
                      currentValue.toLowerCase() === 'yes';
                    const isNo = currentValue.toLowerCase() === 'no';

                    return (
                      <HStack spacing={4}>
                        <Button
                          size="sm"
                          height="2rem"
                          px={4}
                          colorScheme={isYes ? 'green' : 'gray'}
                          variant={isYes ? 'solid' : 'outline'}
                          onClick={() => {
                            const updatedField = {
                              ...field.value,
                              value: 'Yes',
                              metadata: {
                                ...field.value?.metadata,
                              },
                            };
                            field.onChange(updatedField);
                            setValue('New-Telehealth', updatedField, {
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }}
                          borderRadius="md"
                          fontWeight="500"
                        >
                          Yes
                        </Button>
                        <Button
                          size="sm"
                          height="2rem"
                          px={4}
                          colorScheme={isNo ? 'red' : 'gray'}
                          variant={isNo ? 'solid' : 'outline'}
                          onClick={() => {
                            const updatedField = {
                              ...field.value,
                              value: 'No',
                              metadata: {
                                ...field.value?.metadata,
                              },
                            };
                            field.onChange(updatedField);
                            setValue('New-Telehealth', updatedField, {
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }}
                          borderRadius="md"
                          fontWeight="500"
                        >
                          No
                        </Button>
                      </HStack>
                    );
                  }}
                />
              </FormControl>
            {/* Out-Of-State Telehealth Field */}
            <FormControl mb="1.5rem">
              <FormLabel>Out of State Telehealth</FormLabel>
              <Controller
                name="Out-Of-State-Telehealth"
                control={control}
                defaultValue={
                  complexCustomFields['Out-Of-State-Telehealth'] || {
                    value: '',
                    metadata: {},
                    id: undefined,
                    rowId: null,
                    fieldId:
                      customFields.find(
                        (cf) => cf.name === 'Out-Of-State-Telehealth'
                      )?.id || '',
                    startDate: null,
                    endDate: null,
                  }
                }
                render={({ field }) => {
                  const currentValue = field.value?.value || '';
                  const isYes = currentValue.toLowerCase() === 'yes';
                  const isNo = currentValue.toLowerCase() === 'no';

                  return (
                    <HStack spacing={4}>
                      <Button
                        size="sm"
                        height="2rem"
                        px={4}
                        colorScheme={isYes ? 'green' : 'gray'}
                        variant={isYes ? 'solid' : 'outline'}
                        onClick={() => {
                          const updatedField = {
                            ...field.value,
                            value: 'Yes',
                            metadata: {
                              ...field.value?.metadata,
                            },
                          };
                          field.onChange(updatedField);
                          setValue(
                            'Out-Of-State-Telehealth',
                            updatedField,
                            {
                              shouldDirty: true,
                              shouldTouch: true,
                            }
                          );
                        }}
                        borderRadius="md"
                        fontWeight="500"
                      >
                        Yes
                      </Button>
                      <Button
                        size="sm"
                        height="2rem"
                        px={4}
                        colorScheme={isNo ? 'red' : 'gray'}
                        variant={isNo ? 'solid' : 'outline'}
                        onClick={() => {
                          const updatedField = {
                            ...field.value,
                            value: 'No',
                            metadata: {
                              ...field.value?.metadata,
                            },
                          };
                          field.onChange(updatedField);
                          setValue(
                            'Out-Of-State-Telehealth',
                            updatedField,
                            {
                              shouldDirty: true,
                              shouldTouch: true,
                            }
                          );
                        }}
                        borderRadius="md"
                        fontWeight="500"
                      >
                        No
                      </Button>
                    </HStack>
                  );
                }}
              />
            </FormControl>
            {/* Scheduling & Coverage Fields - PCP Only */}
            {providerType === 'Pcp' && (
              <Box mb="1.5rem">
                <Text
                  fontSize="1rem"
                  fontWeight="600"
                  color="gray.700"
                  mb="1rem"
                >
                  Scheduling & Coverage
                </Text>
                <Grid templateColumns="repeat(2, 1fr)" gap="1rem">
                  {/* Takes Walk-ins */}
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="medium">
                      Takes Walk-ins
                    </FormLabel>
                    <Controller
                      name="Takes-Walk-Ins"
                      control={control}
                      defaultValue=""
                      render={({ field }) => {
                        const v = String(field.value || '')
                          .trim()
                          .toLowerCase();
                        const isYes = v === 'yes';
                        const isNo = v === 'no';
                        return (
                          <HStack spacing={2}>
                            <Button
                              size="sm"
                              colorScheme={isYes ? 'green' : 'gray'}
                              variant={isYes ? 'solid' : 'outline'}
                              onClick={() => field.onChange('Yes')}
                            >
                              Yes
                            </Button>
                            <Button
                              size="sm"
                              colorScheme={isNo ? 'red' : 'gray'}
                              variant={isNo ? 'solid' : 'outline'}
                              onClick={() => field.onChange('No')}
                            >
                              No
                            </Button>
                          </HStack>
                        );
                      }}
                    />
                  </FormControl>
                </Grid>
              </Box>
            )}
            {/* Cross Coverage for Sick - PCP ONLY - Full Width Row */}
            {providerType === 'Pcp' && (
              <Box
                mb="1.5rem"
                width="100%"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                overflow="hidden"
                bg="white"
                boxShadow="sm"
                _hover={{ boxShadow: 'md' }}
                transition="all 0.2s"
              >
                <Box
                  p="1rem 1.25rem"
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  bg="gray.50"
                >
                  <Text
                    fontSize="1rem"
                    fontWeight="600"
                    color="gray.700"
                    mb="0"
                  >
                    Cross Coverage for Sick in Clinic
                  </Text>
                  <Text fontSize="0.75rem" color="gray.500" mt={1}>
                    Configure in-clinic cross coverage settings for
                    sick appointments
                  </Text>
                </Box>
                <Box p="1.5rem">
                  <FormFieldErrorBoundary fieldName="Cross Coverage for Sick in Clinic">
                    <Controller
                      name="Cross-Coverage-Sick"
                      control={control}
                      defaultValue={
                        complexCustomFields[
                          'Cross-Coverage-Sick'
                        ] || {
                          value: '',
                          metadata: {
                            providers: [],
                            coverageType: '',
                          },
                          id: undefined,
                          rowId: null,
                          startDate: null,
                          endDate: null,
                          fieldId:
                            customFields.find(
                              (cf) =>
                                cf.name === 'Cross-Coverage-Sick'
                            )?.id || '',
                        }
                      }
                      render={({ field }) => (
                        <CrossCoverageSickSelect
                          scope="clinic"
                          field={field.value}
                          fieldId={
                            customFields.find(
                              (cf) =>
                                cf.name === 'Cross-Coverage-Sick'
                            )?.id ||
                            field.value?.fieldId ||
                            ''
                          }
                          onChange={(updatedField) => {
                            // Ensure fieldId is set if it's missing
                            if (
                              !updatedField.fieldId &&
                              customFields.find(
                                (cf) =>
                                  cf.name === 'Cross-Coverage-Sick'
                              )?.id
                            ) {
                              updatedField.fieldId =
                                customFields.find(
                                  (cf) =>
                                    cf.name === 'Cross-Coverage-Sick'
                                )?.id || '';
                            }

                            // Store the full object in complexCustomFields for later use in transformProviderData
                            setComplexCustomFields((prev) => ({
                              ...prev,
                              'Cross-Coverage-Sick': updatedField,
                            }));

                            // Pass the full CustomFieldValue object to the form field
                            field.onChange(updatedField);
                          }}
                        />
                      )}
                    />
                  </FormFieldErrorBoundary>
                </Box>
              </Box>
            )}
            {/* Closest Urgent Care - PCP ONLY - Full Width Row */}
            {providerType === 'Pcp' && (
              <Box
                mb="1.5rem"
                width="100%"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                overflow="hidden"
                bg="white"
                boxShadow="sm"
                _hover={{ boxShadow: 'md' }}
                transition="all 0.2s"
              >
                <Box
                  p="1rem 1.25rem"
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  bg="gray.50"
                >
                  <Text
                    fontSize="1rem"
                    fontWeight="600"
                    color="gray.700"
                    mb="0"
                  >
                    Closest Urgent Care
                  </Text>
                  <Text fontSize="0.75rem" color="gray.500" mt={1}>
                    Link the closest urgent care clinic for this
                    provider
                  </Text>
                </Box>
                <Box p="1.5rem">
                  <FormFieldErrorBoundary fieldName="Closest Urgent Care">
                    <Controller
                      name="Closest-Urgent-Care"
                      control={control}
                      defaultValue={
                        complexCustomFields['Closest-Urgent-Care'] || {
                          value: '',
                          metadata: { practices: [], notes: '' },
                          id: undefined,
                          rowId: null,
                          startDate: null,
                          endDate: null,
                          fieldId:
                            customFields.find(
                              (cf) => cf.name === 'Closest-Urgent-Care'
                            )?.id || '',
                        }
                      }
                      render={({ field }) => (
                        <ClosestUrgentCareSelect
                          field={field.value}
                          fieldId={
                            customFields.find(
                              (cf) => cf.name === 'Closest-Urgent-Care'
                            )?.id ||
                            field.value?.fieldId ||
                            ''
                          }
                          onChange={(updatedField) => {
                            if (!updatedField.fieldId) {
                              updatedField.fieldId =
                                customFields.find(
                                  (cf) =>
                                    cf.name === 'Closest-Urgent-Care'
                                )?.id || '';
                            }
                            setComplexCustomFields((prev) => ({
                              ...prev,
                              'Closest-Urgent-Care': updatedField,
                            }));
                            field.onChange(updatedField);
                          }}
                        />
                      )}
                    />
                  </FormFieldErrorBoundary>
                </Box>
              </Box>
            )}
            {/* Cross Coverage for Sick in Market - PCP ONLY - Full Width Row */}
            {providerType === 'Pcp' && (
              <Box
                mb="1.5rem"
                width="100%"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                overflow="hidden"
                bg="white"
                boxShadow="sm"
                _hover={{ boxShadow: 'md' }}
                transition="all 0.2s"
              >
                <Box
                  p="1rem 1.25rem"
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  bg="gray.50"
                >
                  <Text
                    fontSize="1rem"
                    fontWeight="600"
                    color="gray.700"
                    mb="0"
                  >
                    Cross Coverage for Sick in Market
                  </Text>
                  <Text fontSize="0.75rem" color="gray.500" mt={1}>
                    Configure in-market cross coverage settings for
                    sick appointments
                  </Text>
                </Box>
                <Box p="1.5rem">
                  <FormFieldErrorBoundary fieldName="Cross Coverage for Sick in Market">
                    <Controller
                      name="Cross-Coverage-for-Sick-Market"
                      control={control}
                      defaultValue={
                        complexCustomFields[
                          'Cross-Coverage-for-Sick-Market'
                        ] || {
                          value: '',
                          metadata: {
                            providers: [],
                            coverageType: '',
                          },
                          id: undefined,
                          rowId: null,
                          startDate: null,
                          endDate: null,
                          fieldId:
                            customFields.find(
                              (cf) =>
                                cf.name ===
                                'Cross-Coverage-for-Sick-Market'
                            )?.id || '',
                        }
                      }
                      render={({ field }) => (
                        <CrossCoverageSickSelect
                          scope="market"
                          field={field.value}
                          fieldId={
                            customFields.find(
                              (cf) =>
                                cf.name ===
                                'Cross-Coverage-for-Sick-Market'
                            )?.id ||
                            field.value?.fieldId ||
                            ''
                          }
                          onChange={(updatedField) => {
                            if (!updatedField.fieldId) {
                              updatedField.fieldId =
                                customFields.find(
                                  (cf) =>
                                    cf.name ===
                                    'Cross-Coverage-for-Sick-Market'
                                )?.id || '';
                            }
                            setComplexCustomFields((prev) => ({
                              ...prev,
                              'Cross-Coverage-for-Sick-Market':
                                updatedField,
                            }));
                            field.onChange(updatedField);
                          }}
                        />
                      )}
                    />
                  </FormFieldErrorBoundary>
                </Box>
              </Box>
            )}
            {/* Next In Line - PCP ONLY - Full Width Row */}
            {providerType === 'Pcp' && (
              <Box mb="1.5rem" width="100%">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">
                    Next In Line
                  </FormLabel>
                  <Controller
                    name="Next-In-Line"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Enter next in line information"
                        size="md"
                        borderRadius="md"
                        borderColor="gray.300"
                        _hover={{ borderColor: 'gray.400' }}
                        _focus={{
                          borderColor: 'blue.500',
                          boxShadow:
                            '0 0 0 1px var(--chakra-colors-blue-500)',
                        }}
                        transition="all 0.2s"
                      />
                    )}
                  />
                </FormControl>
              </Box>
            )}
            {/* Cross Coverage for All Appts - Specialty only */}
            {/* Note: Cross Coverage field is used in backend - keep field name as "Cross-Coverage" */}
            {providerType === 'Specialist' && (
              <Box
                mb="1.5rem"
                width="100%"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                overflow="hidden"
                bg="white"
                boxShadow="sm"
                _hover={{ boxShadow: 'md' }}
                transition="all 0.2s"
              >
                <Box
                  p="1rem 1.25rem"
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  bg="gray.50"
                >
                  <Text
                    fontSize="1rem"
                    fontWeight="600"
                    color="gray.700"
                    mb="0"
                  >
                    Cross Coverage for All Appts
                  </Text>
                  <Text fontSize="0.75rem" color="gray.500" mt={1}>
                    Configure cross coverage settings for all
                    appointment types
                  </Text>
                </Box>
                <Box p="1.5rem">
                  <FormFieldErrorBoundary fieldName="Cross Coverage for All Appts">
                    <Controller
                      name="Cross-Coverage"
                      control={control}
                      defaultValue={
                        complexCustomFields['Cross-Coverage'] || {
                          value: '',
                          metadata: {
                            whoProvidesCoverage: '',
                            whatAppointments: '',
                          },
                          id: undefined,
                          rowId: null,
                          startDate: null,
                          endDate: null,
                          fieldId:
                            customFields.find(
                              (cf) => cf.name === 'Cross-Coverage'
                            )?.id || '',
                        }
                      }
                      render={({ field }) => (
                        <CrossCoverageAllApptsSelect
                          field={field.value}
                          fieldId={
                            customFields.find(
                              (cf) => cf.name === 'Cross-Coverage'
                            )?.id || ''
                          }
                          rowId={providerData?.id} // Provider ID for new fields
                          onChange={(updatedField) => {
                            // Ensure fieldId is set if it's missing
                            if (
                              !updatedField.fieldId &&
                              customFields.find(
                                (cf) => cf.name === 'Cross-Coverage'
                              )?.id
                            ) {
                              updatedField.fieldId =
                                customFields.find(
                                  (cf) => cf.name === 'Cross-Coverage'
                                )?.id || '';
                            }

                            // Store the complex object in complexCustomFields for later use in transformProviderData
                            setComplexCustomFields((prev) => ({
                              ...prev,
                              'Cross-Coverage': updatedField,
                            }));

                            // Pass the full CustomFieldValue object to the form field
                            field.onChange(updatedField);
                          }}
                        />
                      )}
                    />
                  </FormFieldErrorBoundary>
                </Box>
              </Box>
            )}
            {/* Supervision-Physician - Both PCP and Specialty */}
            <Box
              mb="1.5rem"
              width="100%"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              overflow="hidden"
              bg="white"
              boxShadow="sm"
              _hover={{ boxShadow: 'md' }}
              transition="all 0.2s"
            >
              <Box
                p="1rem 1.25rem"
                borderBottom="1px solid"
                borderColor="gray.100"
                bg="gray.50"
              >
                <Text
                  fontSize="1rem"
                  fontWeight="600"
                  color="gray.700"
                  mb="0"
                >
                  Supervising Physician/Supervises Midlevels
                </Text>
                <Text fontSize="0.75rem" color="gray.500" mt={1}>
                  Configure supervising physician relationships
                </Text>
                {/* UX Helper explaining the field label */}
                <Text
                  fontSize="0.75rem"
                  color="blue.600"
                  mt={1}
                  fontStyle="italic"
                >
                  {providerType === 'Pcp' &&
                  /\b(MD|DO)\b/.test(
                    (watch('Credentials') || '').toUpperCase()
                  )
                    ? 'Note: This field will display as "Supervises Midlevels" in the provider detail view since you are an MD/DO.'
                    : providerType === 'Pcp'
                    ? 'Note: This field will display as "Supervising Physician" in the provider detail view for midlevel providers.'
                    : 'Note: This field will display as "Supervising Physician" in the provider detail view for midlevel providers.'}
                </Text>
              </Box>
              <Box p="1.5rem">
                <FormFieldErrorBoundary fieldName="Supervising Physician">
                  <Controller
                    name="Supervision-Physician"
                    control={control}
                    defaultValue={
                      complexCustomFields[
                        'Supervision-Physician'
                      ] || {
                        value: '',
                        metadata: {
                          providers: [],
                          freeTextName: '',
                        },
                        id: undefined,
                        rowId: null,
                        startDate: null,
                        endDate: null,
                        fieldId:
                          customFields.find(
                            (cf) =>
                              cf.name === 'Supervision-Physician'
                          )?.id || '',
                      }
                    }
                    render={({ field }) => (
                      <SupervisingPhysicianSelect
                        field={field.value}
                        onChange={(updatedField) => {
                          // Ensure fieldId is set if it's missing
                          if (
                            !updatedField.fieldId &&
                            customFields.find(
                              (cf) =>
                                cf.name === 'Supervision-Physician'
                            )?.id
                          ) {
                            updatedField.fieldId =
                              customFields.find(
                                (cf) =>
                                  cf.name === 'Supervision-Physician'
                              )?.id || '';
                          }

                          // Store the complex object in complexCustomFields for later use in transformProviderData
                          setComplexCustomFields((prev) => ({
                            ...prev,
                            'Supervision-Physician': updatedField,
                          }));

                          // Pass the full CustomFieldValue object to the form field
                          field.onChange(updatedField);
                        }}
                      />
                    )}
                  />
                </FormFieldErrorBoundary>
              </Box>
            </Box>
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              lineHeight={'120%'}
              color="text.secondary"
              mb="1.06rem"
            >
              Practice Details
            </Text>
            <Flex direction="column" gap="1.25rem" mb="2rem">
              <FormControl
                id="practice-search-section"
                isInvalid={practiceError}
                isRequired={!isEditMode}
              >
                <FormLabel
                  fontSize="sm"
                  fontWeight={600}
                  color="gray.700"
                  mb={1.5}
                >
                  Search & add a practice
                </FormLabel>
                <FormHelperText
                  mt={0}
                  mb={2}
                  fontSize="xs"
                  color="gray.500"
                >
                  Type a practice name and click Search, then select from
                  the results below.
                </FormHelperText>
                <PracticeSearchSelect
                  field={{
                    name: 'relatedPractices',
                    value: '',
                    onChange: (value) =>
                      handleAddPractice({
                        label: Array.isArray(value) ? '' : value.label,
                        value: Array.isArray(value) ? '' : value.value,
                      }),
                  }}
                  isInvalid={practiceError}
                />
                <FormErrorMessage mt={2}>
                  At least one practice must be added before saving.
                </FormErrorMessage>
              </FormControl>
              <Box>
                {providerBridge.map((bridgeItem) => {
                  const practiceName =
                    bridgeItem.tempName || // Use the temporary name from the providerBridge item if available
                    providerData?.relatedPractices?.find(
                      (practice: Practice) =>
                        practice.id === bridgeItem.practiceId
                    )?.name;

                  return (
                    <Box
                      key={bridgeItem.id ?? bridgeItem.practiceId}
                      border="1px solid"
                      borderColor="blue.100"
                      borderRadius="lg"
                      overflow="hidden"
                      mb={4}
                      bg="white"
                      boxShadow="0 1px 4px rgba(0,0,0,0.05)"
                    >
                      {/* Card header */}
                      <Flex
                        justify="space-between"
                        align="center"
                        px={4}
                        py={3}
                        bg="blue.50"
                        borderBottom="1px solid"
                        borderColor="blue.100"
                      >
                        <HStack spacing={2.5}>
                          <Box color="blue.500" flexShrink={0}>
                            <svg
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M19 3H5a2 2 0 0 0-2 2v16h18V5a2 2 0 0 0-2-2zm-7 14H8v-2h4v2zm0-4H8v-2h4v2zm0-4H8V7h4v2zm6 8h-4v-2h4v2zm0-4h-4v-2h4v2zm0-4h-4V7h4v2z" />
                            </svg>
                          </Box>
                          <Text
                            fontWeight={700}
                            fontSize="sm"
                            color="blue.900"
                          >
                            {practiceName ||
                              `Practice ID: ${bridgeItem.practiceId}`}
                          </Text>
                        </HStack>
                        {bridgeItem.tempName && (
                          <Badge
                            colorScheme="yellow"
                            fontSize="xs"
                            px={2}
                            py={0.5}
                            borderRadius="md"
                          >
                            Unsaved
                          </Badge>
                        )}
                      </Flex>

                      {/* Card body */}
                      <Box p={4}>
                      <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                        <FormControl>
                          <FormLabel>On PAC</FormLabel>
                          <Select
                            value={
                              bridgeItem.onPac
                                ? 'yes'
                                : bridgeItem.hospitalFollowupPac && bridgeItem.sickCrossCoveragePac
                                ? 'hfus_and_sick'
                                : bridgeItem.hospitalFollowupPac
                                ? 'hfus_only'
                                : bridgeItem.sickCrossCoveragePac
                                ? 'sick_only'
                                : 'no'
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              const updatedBridge =
                                providerBridge.map((pb) =>
                                  pb.practiceId ===
                                  bridgeItem.practiceId
                                    ? {
                                        ...pb,
                                        onPac: v === 'yes',
                                        hospitalFollowupPac:
                                          v === 'hfus_only' || v === 'hfus_and_sick'
                                            ? true
                                            : null,
                                        sickCrossCoveragePac:
                                          v === 'sick_only' || v === 'hfus_and_sick'
                                            ? true
                                            : null,
                                      }
                                    : pb
                                );
                              setProviderBridge(updatedBridge);
                            }}
                          >
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                            <option value="hfus_only">Hospital Follow-Up Scheduling Only</option>
                            <option value="sick_only">Sick Cross Coverage Only</option>
                            <option value="hfus_and_sick">Hospital FU and Sick Cross Coverage Only</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Start Date</FormLabel>
                          <Input
                            type="date"
                            value={bridgeItem.startDate}
                            onChange={(e) => {
                              const updatedBridge =
                                providerBridge.map((pb) =>
                                  pb.practiceId ===
                                  bridgeItem.practiceId
                                    ? {
                                        ...pb,
                                        startDate: e.target.value,
                                      }
                                    : pb
                                );
                              setProviderBridge(updatedBridge);
                            }}
                          />
                        </FormControl>
                        <FormControl>
                          <Flex align="center">
                            <FormLabel>End Date</FormLabel>
                            <Tooltip label="To remove or terminate a provider from a practice, set the end date. If the date is today or in the past, the provider will be unlinked immediately. If the date is in the future, the provider will be terminated on that date.">
                              <InfoOutlineIcon
                                color="blue.500"
                                cursor="pointer"
                                boxSize={5}
                                marginBottom={'0.625rem'}
                              />
                            </Tooltip>
                          </Flex>
                          <Input
                            type="date"
                            value={bridgeItem.endDate ?? ''}
                            onChange={(e) => {
                              const updatedBridge =
                                providerBridge.map((pb) =>
                                  pb.practiceId ===
                                  bridgeItem.practiceId
                                    ? {
                                        ...pb,
                                        endDate: e.target.value,
                                      }
                                    : pb
                                );
                              setProviderBridge(updatedBridge);
                            }}
                          />
                        </FormControl>
                      </Grid>

                      {/* Copy Insurance Dialog - Shows automatically when practice is linked */}

                      {/* Athena Department Information */}
                      <Box
                        mt="1rem"
                        pt="1rem"
                        borderTop="1px solid"
                        borderColor="gray.200"
                      >
                        <Text
                          fontSize="0.875rem"
                          fontWeight="600"
                          color="gray.700"
                          mb="0.75rem"
                        >
                          Athena Department Information
                        </Text>
                        <Grid
                          templateColumns="repeat(2, 1fr)"
                          gap="1rem"
                        >
                          <FormControl gridColumn="span 2">
                            <FormLabel fontSize="sm">
                              Athena Department Name
                            </FormLabel>
                            <Input
                              placeholder="Enter department name"
                              value={
                                bridgeItem.metadata?.[
                                  'Athena-Department'
                                ] || ''
                              }
                              onChange={(e) => {
                                const departmentName = e.target.value;
                                const updatedBridge =
                                  providerBridge.map((pb) =>
                                    pb.practiceId ===
                                    bridgeItem.practiceId
                                      ? {
                                          ...pb,
                                          metadata: {
                                            ...pb.metadata,
                                            'Athena-Department':
                                              departmentName,
                                            // Clear department ID when user types manually
                                            'Athena-Department-Id':
                                              '',
                                          },
                                        }
                                      : pb
                                  );
                                setProviderBridge(updatedBridge);
                              }}
                            />
                            <FormHelperText fontSize="xs" mt="0.5rem">
                              Enter department name manually or use
                              the helper below to autofill from
                              tablespace
                            </FormHelperText>

                            {/* Department Autofill Helper Section */}
                            <Box
                              mt="1rem"
                              p="1rem"
                              border="1px solid"
                              borderColor="gray.200"
                              borderRadius="md"
                              bg="gray.50"
                            >
                              <Text
                                fontSize="0.75rem"
                                fontWeight="600"
                                color="gray.700"
                                mb="0.75rem"
                              >
                                Department Autofill Helper
                              </Text>
                              <Text
                                fontSize="0.7rem"
                                color="gray.600"
                                mb="1rem"
                              >
                                Enter tablespace to load departments,
                                then select one to autofill the
                                department name above
                              </Text>

                              {(() => {
                                const tablespace =
                                  bridgeItem.metadata?.[
                                    'Athena-Tablespace'
                                  ]?.trim() || '';
                                const departments = tablespace
                                  ? departmentsByTablespace[
                                      tablespace
                                    ] || []
                                  : [];
                                const isLoading = tablespace
                                  ? loadingDepartments[tablespace]
                                  : false;
                                const currentSelectedDept =
                                  selectedDeptForAutofill[
                                    bridgeItem.practiceId
                                  ] || '';

                                return (
                                  <VStack spacing={3} align="stretch">
                                    <HStack spacing={2} align="flex-end">
                                      <FormControl flex={1}>
                                        <FormLabel fontSize="xs">
                                          Tablespace
                                        </FormLabel>
                                        <Input
                                          size="sm"
                                          placeholder="Enter tablespace"
                                          value={tablespace}
                                          onChange={(e) => {
                                            const newTablespace =
                                              e.target.value;
                                            const updatedBridge =
                                              providerBridge.map(
                                                (pb) =>
                                                  pb.practiceId ===
                                                  bridgeItem.practiceId
                                                    ? {
                                                        ...pb,
                                                        metadata: {
                                                          ...pb.metadata,
                                                          'Athena-Tablespace':
                                                            newTablespace,
                                                        },
                                                      }
                                                    : pb
                                              );
                                            setProviderBridge(
                                              updatedBridge
                                            );
                                            // Clear selected department when tablespace changes
                                            setSelectedDeptForAutofill(
                                              (prev) => ({
                                                ...prev,
                                                [bridgeItem.practiceId]:
                                                  '',
                                              })
                                            );
                                          }}
                                        />
                                      </FormControl>
                                      <Button
                                        size="sm"
                                        colorScheme="blue"
                                        onClick={() => {
                                          const currentTablespace =
                                            bridgeItem.metadata?.[
                                              'Athena-Tablespace'
                                            ]?.trim();
                                          if (currentTablespace) {
                                            fetchDepartments(currentTablespace);
                                          }
                                        }}
                                        isDisabled={
                                          !bridgeItem.metadata?.[
                                            'Athena-Tablespace'
                                          ]?.trim()
                                        }
                                        isLoading={isLoading}
                                      >
                                        Load Departments
                                      </Button>
                                    </HStack>
                                    <FormControl>
                                      <FormLabel fontSize="xs">
                                        Department
                                      </FormLabel>
                                      <Select
                                        size="sm"
                                        placeholder={
                                          isLoading
                                            ? 'Loading...'
                                            : departments.length > 0
                                            ? 'Select department'
                                            : 'Load departments first'
                                        }
                                        value={currentSelectedDept}
                                        onChange={(e) => {
                                          setSelectedDeptForAutofill(
                                            (prev) => ({
                                              ...prev,
                                              [bridgeItem.practiceId]:
                                                e.target.value,
                                            })
                                          );
                                        }}
                                        isDisabled={
                                          isLoading ||
                                          departments.length === 0
                                        }
                                      >
                                        {departments.map((dept) => (
                                          <option
                                            key={dept.id}
                                            value={dept.name}
                                          >
                                            {dept.name}
                                          </option>
                                        ))}
                                      </Select>
                                    </FormControl>

                                    {currentSelectedDept && (
                                      <Button
                                        size="sm"
                                        colorScheme="green"
                                        onClick={() => {
                                          const selectedDept =
                                            departments.find(
                                              (d) =>
                                                d.name ===
                                                currentSelectedDept
                                            );
                                          const updatedBridge =
                                            providerBridge.map((pb) =>
                                              pb.practiceId ===
                                              bridgeItem.practiceId
                                                ? {
                                                    ...pb,
                                                    metadata: {
                                                      ...pb.metadata,
                                                      'Athena-Department':
                                                        currentSelectedDept,
                                                      'Athena-Department-Id':
                                                        selectedDept?.id ||
                                                        '',
                                                    },
                                                  }
                                                : pb
                                            );
                                          setProviderBridge(
                                            updatedBridge
                                          );
                                        }}
                                      >
                                        Autofill Department Name
                                      </Button>
                                    )}

                                    {departments.length > 0 && (
                                      <Text
                                        fontSize="0.7rem"
                                        color="gray.600"
                                      >
                                        {departments.length}{' '}
                                        department
                                        {departments.length !== 1
                                          ? 's'
                                          : ''}{' '}
                                        loaded
                                      </Text>
                                    )}
                                  </VStack>
                                );
                              })()}
                            </Box>
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">
                              Athena Department Link
                            </FormLabel>
                            <Input
                              placeholder="Enter URL starting with www or https://"
                              value={
                                bridgeItem.metadata?.[
                                  'Athena-Link'
                                ] || ''
                              }
                              onChange={(e) => {
                                const updatedBridge =
                                  providerBridge.map((pb) =>
                                    pb.practiceId ===
                                    bridgeItem.practiceId
                                      ? {
                                          ...pb,
                                          metadata: {
                                            ...pb.metadata,
                                            'Athena-Link':
                                              e.target.value,
                                          },
                                        }
                                      : pb
                                  );
                                setProviderBridge(updatedBridge);
                              }}
                            />
                            <FormHelperText fontSize="xs">
                              Valid URL - https:// will be added
                              automatically
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                      </Box>

                      {bridgeItem.tempName && (
                        <Button
                          colorScheme="red"
                          variant="ghost"
                          size="sm"
                          mt={2}
                          onClick={() =>
                            setProviderBridge(
                              providerBridge.filter(
                                (pb) =>
                                  pb.practiceId !==
                                  bridgeItem.practiceId
                              )
                            )
                          }
                        >
                          ✖ Remove
                        </Button>
                      )}
                      </Box> {/* card body */}
                    </Box>
                  );
                })}
              </Box>
            </Flex>
          </Flex>
        </form>

        {/* Save validation helper text */}
        {(() => {
          const missing: string[] = [];
          if (!watchedProviderType || watchedProviderType.toString().trim() === '') {
            missing.push('Select a Provider Type');
          }
          if (!isEditMode && providerBridge.length === 0) {
            missing.push('Associate at least one practice');
          }
          if (!watchedNpi) missing.push('Enter an NPI');
          if (!watchedFirstname) missing.push('Enter a First Name');
          if (!watchedLastname) missing.push('Enter a Last Name');
          if (missing.length === 0) return null;
          return (
            <Box
              mt="1.5rem"
              p="3"
              borderRadius="md"
              bg="orange.50"
              borderLeft="4px solid"
              borderLeftColor="orange.400"
            >
              <Text fontSize="sm" fontWeight="semibold" color="orange.700" mb="1">
                Complete the following before saving:
              </Text>
              <VStack align="start" spacing={0.5}>
                {missing.map((msg) => (
                  <Text key={msg} fontSize="sm" color="orange.600">
                    • {msg}
                  </Text>
                ))}
              </VStack>
            </Box>
          );
        })()}

        {/* Footer - Moved below insurance section */}
        <Flex gap="2rem" mt="2rem" alignItems="center" mb="3rem">
          <Button
            variant="outline"
            colorScheme="red"
            onClick={cancelDialog.onOpen}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            colorScheme="blue"
            background={'background.brandLight'}
            isLoading={saving || isFetchingPlans}
            loadingText={isFetchingPlans ? 'Fetching plans...' : (isEditMode ? 'Updating...' : 'Saving...')}
            isDisabled={saving || isFetchingPlans || !isFormValidForSubmission()}
          >
            Save Provider
          </Button>
        </Flex>

        <AlertDialog
          isOpen={cancelDialog.isOpen}
          leastDestructiveRef={cancelRef}
          onClose={cancelDialog.onClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Discard Changes?
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to cancel? You might lose your
                changes.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button
                  ref={cancelRef}
                  onClick={cancelDialog.onClose}
                >
                  No
                </Button>
                <Button
                  colorScheme="red"
                  onClick={() =>
                    isEditMode && providerId
                      ? navigate(`/providers/${providerId}`)
                      : navigate('/providers')
                  }
                  ml={3}
                >
                  Yes, Cancel
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Simple Copy Insurance Confirmation Dialog */}
        <AlertDialog
          isOpen={copyInsuranceDialog.isOpen}
          leastDestructiveRef={cancelRef}
          onClose={() => {
            setCopyInsuranceDialog({ isOpen: false, newPracticeId: null, practicesWithInsurance: [] });
            setSelectedSourcePracticeId(null);
          }}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Copy Insurance Plans?
              </AlertDialogHeader>
              <AlertDialogBody>
                <VStack spacing={4} align="stretch">
                  {copyInsuranceDialog.practicesWithInsurance.length === 1 ? (
                    <>
                      {selectedSourcePracticeId === copyInsuranceDialog.newPracticeId ? (
                        <Text>
                          Found <strong>{practiceNames.sourcePlanCount} insurance plan{practiceNames.sourcePlanCount !== 1 ? 's' : ''}</strong> in{' '}
                          <strong>{practiceNames.newPracticeName}</strong>. Copy these plans to this provider?
                        </Text>
                      ) : (
                        <Text>
                          Copy <strong>{practiceNames.sourcePlanCount} insurance plan{practiceNames.sourcePlanCount !== 1 ? 's' : ''}</strong> from{' '}
                          <strong>{practiceNames.sourcePracticeName}</strong> to{' '}
                          <strong>{practiceNames.newPracticeName}</strong>?
                        </Text>
                      )}
                    </>
                  ) : (
                    <>
                      <Text>
                        This provider has insurance plans in {copyInsuranceDialog.practicesWithInsurance.length} other practices.
                        Select which practice to copy from:
                      </Text>
                      <FormControl>
                        <FormLabel>Copy from practice:</FormLabel>
                        <Select
                          value={selectedSourcePracticeId || ''}
                          onChange={(e) => setSelectedSourcePracticeId(e.target.value)}
                        >
                          <option value="">Select a practice</option>
                          {copyInsuranceDialog.practicesWithInsurance.map((practice) => (
                            <option key={practice.id} value={practice.id}>
                              {practice.name} ({practice.planCount} plan{practice.planCount !== 1 ? 's' : ''})
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                      {selectedSourcePracticeId && (
                        <Box p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                          <Text fontSize="sm">
                            Will copy <strong>{practiceNames.sourcePlanCount} plan{practiceNames.sourcePlanCount !== 1 ? 's' : ''}</strong> from{' '}
                            <strong>{practiceNames.sourcePracticeName}</strong> to{' '}
                            <strong>{practiceNames.newPracticeName}</strong>
                          </Text>
                        </Box>
                      )}
                    </>
                  )}
                </VStack>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button
                  onClick={() => {
                    setCopyInsuranceDialog({ isOpen: false, newPracticeId: null, practicesWithInsurance: [] });
                    setSelectedSourcePracticeId(null);
                  }}
                >
                  Skip
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleCopyInsurance}
                  isLoading={isCopyingInsurance}
                  isDisabled={!selectedSourcePracticeId}
                  ml={3}
                >
                  Copy Insurance
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* New Provider Insurance Setup Screen */}
        {showInsuranceSetup && newProviderSetupData && (
          <NewProviderInsuranceSetup
            providerId={newProviderSetupData.providerId}
            practiceId={newProviderSetupData.practiceId}
            practiceName={newProviderSetupData.practiceName}
            providerName={newProviderSetupData.providerName}
            onComplete={() => {
              setShowInsuranceSetup(false);
              setNewProviderSetupData(null);
            }}
          />
        )}

      </Flex>
    </>
  );
};

export default CreateProvider;
